import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';
import pako from 'pako';
import logo from '../assets/logo.png';
import { getTulpiaanToken } from '../utils/token';

// ─── Kleuren & accenten ───────────────────────────────────────────────────────
const C = {
  wit: '#ffffff', papier: '#faf9f6', grijs50: '#f5f4f1',
  grijs100: '#edecea', grijs200: '#d8d6d0', grijs400: '#9e9b93',
  grijs600: '#6b6860', grijs800: '#3a3830', inkt: '#1e1c17',
  fout: '#c04a3a', foutBg: '#fdf2f0',
};

const ACCENTEN = [
  { naam: 'Goud',       kleur: '#cbad74' },
  { naam: 'Blauw',      kleur: '#3a6090' },
  { naam: 'Groen',      kleur: '#3a7040' },
  { naam: 'Terracotta', kleur: '#c0533a' },
  { naam: 'Antraciet',  kleur: '#4a4a4a' },
  { naam: 'Bordeaux',   kleur: '#7a2535' },
];


function hexRgb(hex) {
  return { r: parseInt(hex.slice(1,3),16), g: parseInt(hex.slice(3,5),16), b: parseInt(hex.slice(5,7),16) };
}
function accentLicht(hex) { const {r,g,b} = hexRgb(hex); return `rgba(${r},${g},${b},0.13)`; }
function accentTekst(hex) {
  const {r,g,b} = hexRgb(hex);
  return (0.299*r + 0.587*g + 0.114*b) > 145 ? '#1e1c17' : '#ffffff';
}

// ─── Data makers ──────────────────────────────────────────────────────────────
const uid = () => Date.now() + Math.random();
const legeWerk    = () => ({ id: uid(), bedrijf:'', functie:'', periode:'', intro:'', bullets:'', resultaten:'' });
const legeOpl     = () => ({ id: uid(), instelling:'', richting:'', periode:'' });
const legeTaal    = () => ({ id: uid(), taal:'', niveau:'B2' });
const legeCert    = () => ({ id: uid(), naam:'', jaar:'' });
const legeVrij    = () => ({ id: uid(), organisatie:'', rol:'', periode:'' });
const legeEigen   = () => ({ id: `eigen_${uid()}`, titel:'', tekst:'' });

const VASTE = ['werkervaring','opleiding','vaardigheden','talen','certificaten','vrijwilligerswerk'];
const niveaus = ['A1','A2','B1','B2','C1','C2','Moedertaal'];

const beginData = {
  naam:'', functietitel:'', foto:null,
  email:'', telefoon:'', locatie:'', linkedin:'', website:'',
  samenvatting:'',
  werkervaring: [legeWerk()],
  opleiding:    [legeOpl()],
  vaardigheden: '',
  talen:        [legeTaal()],
  certificaten: [legeCert()],
  vrijwilligerswerk: [legeVrij()],
};

const beginZichtbaar = { werkervaring:true, opleiding:true, vaardigheden:true, talen:true, certificaten:true, vrijwilligerswerk:true };

// ─── Bewaarcode (client-side serialisatie van complete CV-state) ─────────────
// Versie 1: { v, ts, d } waarbij d alle state-onderdelen bevat zodat je exact
// terugkomt waar je was — inclusief template, kleuren, sectievolgorde, etc.
const BEWAARCODE_PREFIX = 'TLP1-';
const BEWAARCODE_VERSIE = 1;

function toBase64Url(bytes) {
  let binair = '';
  // chunk-gewijs om 'Maximum call stack' te vermijden bij grote arrays (foto's)
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binair += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binair).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(str) {
  let s = str.replace(/-/g, '+').replace(/_/g, '/');
  while (s.length % 4) s += '=';
  return Uint8Array.from(atob(s), c => c.charCodeAt(0));
}

function generateSaveCode(snapshot) {
  const wrap = {
    v: BEWAARCODE_VERSIE,
    ts: new Date().toISOString(),
    d: snapshot,
  };
  const json = JSON.stringify(wrap);
  const gz = pako.gzip(json);
  return BEWAARCODE_PREFIX + toBase64Url(gz);
}

function migrate(parsed) {
  if (!parsed || typeof parsed.v !== 'number' || !parsed.d) {
    throw new Error('Bewaarcode is leeg of incompleet.');
  }
  if (parsed.v === 1) return parsed.d;
  // toekomstige versies komen hier:
  // if (parsed.v === 2) return migrateV2NaarV1(parsed.d);
  throw new Error(`Onbekende bewaarcode-versie (${parsed.v}). Werk de tool bij of gebruik een nieuwere code.`);
}

function loadSaveCode(rauw) {
  if (typeof rauw !== 'string') throw new Error('Bewaarcode niet herkend — controleer of je hem volledig hebt geplakt.');
  const code = rauw.trim().replace(/\s+/g, ''); // strip whitespace die mailclients soms inbreken
  if (!code.startsWith(BEWAARCODE_PREFIX)) {
    throw new Error('Bewaarcode niet herkend — een geldige code begint met "TLP1-".');
  }
  const body = code.slice(BEWAARCODE_PREFIX.length);
  if (body.length < 8) {
    throw new Error('Bewaarcode lijkt onvolledig — controleer of je hem helemaal hebt gekopieerd.');
  }
  let json;
  try {
    const bytes = fromBase64Url(body);
    json = pako.ungzip(bytes, { to: 'string' });
  } catch (e) {
    throw new Error('Bewaarcode niet herkend — controleer of je hem volledig hebt geplakt.');
  }
  let parsed;
  try { parsed = JSON.parse(json); }
  catch { throw new Error('Bewaarcode is beschadigd — kun je hem opnieuw kopiëren of het bestand opnieuw uploaden?'); }
  return migrate(parsed);
}

function bestandsnaamCV(cv) {
  const naam = (cv?.naam || 'cv').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'cv';
  const d = new Date();
  const ymd = `${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}`;
  return `cv-${naam}-${ymd}.tulpiaan-cv`;
}

// ─── CSS injectie ─────────────────────────────────────────────────────────────
function injectGlobalCss() {
  if (document.getElementById('cv-global')) return;
  const el = document.createElement('style');
  el.id = 'cv-global';
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'DM Sans', sans-serif; background: ${C.papier}; }
    @keyframes spin    { to { transform: rotate(360deg); } }
    @keyframes fadeUp  { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
    .ai-knop:hover     { background: #f0e6cc !important; border-color: #cbad74 !important; }
    .verwijder:hover   { background: ${C.foutBg} !important; border-color: ${C.fout} !important; color: ${C.fout} !important; }
    .toevoeg:hover     { background: ${C.grijs50} !important; }
    .pijl:hover:not(:disabled) { background: ${C.grijs100} !important; }
    .pijl:disabled     { opacity: .25; cursor: default; }
    .tpl-btn:hover     { opacity: .85; }
    .exp-ghost:hover   { background: ${C.grijs50} !important; }
    @media print {
      body { display: none !important; }
    }
    @media (max-width: 900px) {
      .split-layout { flex-direction: column !important; }
      .form-panel   { border-right: none !important; border-bottom: 1px solid ${C.grijs100} !important; max-height: 50vh; }
    }
  `;
  document.head.appendChild(el);
}

function updateAccentCss(hex) {
  let el = document.getElementById('cv-accent');
  if (!el) { el = document.createElement('style'); el.id = 'cv-accent'; document.head.appendChild(el); }
  el.textContent = `input:focus,textarea:focus,select:focus{outline:none!important;border-color:${hex}!important;box-shadow:0 0 0 3px ${accentLicht(hex)}!important;}`;
}

// ─── Voortgang ────────────────────────────────────────────────────────────────
function voortgang(cv) {
  let p = 0, m = 0;
  const chk = v => { m++; if (v && String(v).trim()) p++; };
  chk(cv.naam); chk(cv.email); chk(cv.samenvatting); chk(cv.vaardigheden);
  cv.werkervaring.forEach(w => { chk(w.bedrijf); chk(w.functie); chk(w.intro || w.bullets); });
  cv.opleiding.forEach(o => { chk(o.instelling); chk(o.richting); });
  return Math.round((p / m) * 100);
}

// ─── AI helpers ───────────────────────────────────────────────────────────────
function bouwPrompt(type, tekst) {
  const slot = 'Geef alleen de verbeterde tekst terug — geen uitleg, geen aanhalingstekens, geen extra opmaak. Voeg nooit nieuwe informatie, cijfers of feiten toe die de gebruiker niet zelf heeft aangeleverd.';
  if (type === 'samenvatting')
    return `Verbeter de volgende profieltekst voor een cv. Corrigeer spelling en grammatica. Maak zinnen actiever en helderder waar dat de tekst sterker maakt. Blijf strikt bij de inhoud die de gebruiker heeft aangeleverd — voeg niets toe en verzin niets. Maximaal 4 zinnen. Vermijd holle woorden als "gedreven", "resultaatgericht", "passie", "enthousiast". ${slot}\n\nOriginele tekst:\n${tekst}`;
  if (type === 'intro')
    return `Verbeter de volgende openingszin voor een cv. Corrigeer spelling en grammatica. Maak de zin actiever als dat de strekking versterkt. Blijf strikt bij de inhoud die de gebruiker heeft aangeleverd — voeg geen nieuwe informatie toe. Één zin. ${slot}\n\nOriginele zin:\n${tekst}`;
  if (type === 'bullets')
    return `Verbeter de volgende cv-bullets. Corrigeer spelling en grammatica. Maak zinnen actiever waar dat logisch is. Voeg geen nieuwe taken, resultaten of cijfers toe die de gebruiker niet heeft aangeleverd. Geef precies evenveel bullets terug als ingevoerd, één per regel, zonder opsommingstekens. ${slot}\n\nOriginele bullets:\n${tekst}`;
  if (type === 'resultaten')
    return `Verbeter de volgende tekst over resultaten voor een cv. Corrigeer spelling en grammatica. Maak de formulering helderder. Verzin geen cijfers of meetbare uitkomsten die de gebruiker niet zelf heeft aangeleverd. ${slot}\n\nOriginele tekst:\n${tekst}`;
  return `Verbeter de volgende cv-tekst. Corrigeer spelling en grammatica. Blijf strikt bij de aangeleverde inhoud. ${slot}\n\nOriginele tekst:\n${tekst}`;
}

async function roepAiAan(type, tekst) {
  const res = await fetch('/api/claude', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-tulpiaan-token': await getTulpiaanToken(),
    },
    body: JSON.stringify({
      max_tokens: 600,
      messages: [{ role: 'user', content: bouwPrompt(type, tekst) }],
    }),
  });
  const data = await res.json();
  return data.content?.[0]?.text || tekst;
}

// ─── UI atoms ─────────────────────────────────────────────────────────────────
const inputStijl = {
  width:'100%', border:`1.5px solid ${C.grijs200}`, borderRadius:7,
  padding:'9px 11px', fontSize:14, color:C.inkt, background:C.wit,
  fontFamily:"'DM Sans', sans-serif", transition:'all .15s ease',
};
const taStijl = { ...inputStijl, resize:'vertical', minHeight:80 };

function Veld({ label, children }) {
  return (
    <div style={{ marginBottom:14 }}>
      {label && <label style={{ display:'block', fontSize:11, fontWeight:600, color:C.grijs800, marginBottom:5, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</label>}
      {children}
    </div>
  );
}
function Rij({ children, gap=12 }) {
  return <div style={{ display:'grid', gridTemplateColumns:`repeat(${React.Children.count(children)},1fr)`, gap }}>{children}</div>;
}

// ─── Pijl-knopje voor volgorde ────────────────────────────────────────────────
function Pijl({ richting, disabled, onClick }) {
  return (
    <button className="pijl" onClick={onClick} disabled={disabled}
      style={{ border:`1px solid ${C.grijs200}`, background:C.wit, borderRadius:5, width:26, height:26, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, color:C.grijs600, transition:'all .12s ease' }}>
      {richting === 'op' ? '▲' : '▼'}
    </button>
  );
}

// ─── FormSectie ───────────────────────────────────────────────────────────────
function FormSectie({ titel, zichtbaar, onToggle, onOp, onNeer, isEerste, isLaatste, children, accent }) {
  return (
    <div style={{ marginBottom:20, background:C.wit, border:`1px solid ${C.grijs100}`, borderRadius:10, overflow:'hidden' }}>
      <div style={{ display:'flex', alignItems:'center', gap:6, padding:'11px 16px', background:C.grijs50, borderBottom: zichtbaar ? `1px solid ${C.grijs100}` : 'none' }}>
        {onOp && (
          <div style={{ display:'flex', gap:3, flexShrink:0 }}>
            <Pijl richting="op"   disabled={isEerste} onClick={onOp} />
            <Pijl richting="neer" disabled={isLaatste} onClick={onNeer} />
          </div>
        )}
        <span style={{ fontFamily:"'Lora', serif", fontSize:14, fontWeight:600, color:C.inkt, flex:1 }}>{titel}</span>
        {onToggle && (
          <button onClick={onToggle} style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, color:C.grijs600, fontFamily:"'DM Sans', sans-serif", padding:'2px 6px', flexShrink:0 }}>
            {zichtbaar ? 'Verbergen' : 'Tonen'}
          </button>
        )}
      </div>
      {zichtbaar && <div style={{ padding:'14px 16px' }}>{children}</div>}
    </div>
  );
}

// ─── AI knop + suggestie ──────────────────────────────────────────────────────
function AiKnop({ tekst, type, onResultaat, accent }) {
  const [bezig, setBezig] = useState(false);
  async function verbeter() {
    if (!tekst?.trim()) return;
    setBezig(true);
    try {
      const r = await roepAiAan(type, tekst);
      onResultaat(r);
    } catch { alert('Er is iets misgegaan met de AI-verbetering. Probeer het over een minuut opnieuw.'); }
    finally { setBezig(false); }
  }
  return (
    <button className="ai-knop" onClick={verbeter} disabled={bezig}
      style={{ display:'inline-flex', alignItems:'center', gap:6, border:`1.5px solid ${C.grijs200}`, background:C.wit, borderRadius:7, padding:'6px 12px', fontSize:12, fontWeight:600, color: accent || '#a8894f', cursor: bezig ? 'wait' : 'pointer', fontFamily:"'DM Sans', sans-serif", transition:'all .15s ease', marginTop:4 }}>
      {bezig
        ? <span style={{ width:12, height:12, border:`2px solid ${accent||'#cbad74'}`, borderTopColor:'transparent', borderRadius:'50%', animation:'spin .7s linear infinite', display:'inline-block' }} />
        : '✦'}
      {bezig ? 'Bezig…' : 'Verbeter met AI'}
    </button>
  );
}

function AiSuggestie({ tekst, accent, onOvernemen, onNegeren }) {
  if (!tekst) return null;
  const bg = accentLicht(accent || '#cbad74');
  return (
    <div style={{ marginTop:8, border:`1.5px solid ${accent||'#cbad74'}`, borderRadius:8, background:bg, padding:12, animation:'fadeUp .25s ease' }}>
      <div style={{ fontSize:11, fontWeight:600, color: accent||'#cbad74', marginBottom:6, textTransform:'uppercase', letterSpacing:'.05em' }}>✦ AI-suggestie</div>
      <p style={{ fontSize:13, color:C.inkt, lineHeight:1.65, whiteSpace:'pre-wrap' }}>{tekst}</p>
      <div style={{ display:'flex', gap:8, marginTop:8 }}>
        <button onClick={onOvernemen} style={{ background: accent||'#cbad74', color: accentTekst(accent||'#cbad74'), border:'none', borderRadius:6, padding:'6px 13px', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans', sans-serif" }}>Overnemen</button>
        <button onClick={onNegeren}   style={{ background:'none', border:`1.5px solid ${C.grijs200}`, borderRadius:6, padding:'6px 13px', fontSize:12, color:C.grijs600, cursor:'pointer', fontFamily:"'DM Sans', sans-serif" }}>Negeren</button>
      </div>
    </div>
  );
}

// ─── Voortgangsbalk ───────────────────────────────────────────────────────────
function VoortgangsBalk({ pct, accent }) {
  return (
    <div className="geen-print" style={{ padding:'8px 24px 0', display:'flex', alignItems:'center', gap:12 }}>
      <div style={{ flex:1, height:5, background:C.grijs100, borderRadius:99, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct}%`, background: accent||'#cbad74', borderRadius:99, transition:'width .4s ease' }} />
      </div>
      <span style={{ fontSize:11, fontWeight:600, color:C.grijs600, minWidth:32 }}>{pct}%</span>
    </div>
  );
}

// ─── Kleurkiezer ─────────────────────────────────────────────────────────────
function KleurKiezer({ label, waarde, setWaarde, kleurenset = ACCENTEN }) {
  const isEigen = !kleurenset.find(a => a.kleur === waarde);
  const inputRef = React.useRef(null);

  return (
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <span style={{ fontSize:11, fontWeight:600, color:C.grijs600, textTransform:'uppercase', letterSpacing:'.05em', minWidth:72 }}>{label}</span>
      {kleurenset.map(a => (
        <button key={a.kleur} onClick={() => setWaarde(a.kleur)} title={a.naam}
          style={{ width:22, height:22, borderRadius:'50%', background:a.kleur, border: waarde === a.kleur ? `2px solid ${C.inkt}` : '2px solid transparent', cursor:'pointer', boxShadow: waarde === a.kleur ? '0 0 0 2px white inset' : 'none', transition:'all .15s ease', flexShrink:0 }} />
      ))}

      {/* Eigen kleur — regenboog-cirkel die de native picker opent */}
      <button
        onClick={() => inputRef.current?.click()}
        title="Kies eigen kleur"
        style={{
          width:22,
          height:22,
          borderRadius:'50%',
          background: isEigen
            ? waarde
            : 'conic-gradient(from 0deg, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
          border: isEigen ? `2px solid ${C.inkt}` : '2px solid #fff',
          cursor:'pointer',
          boxShadow: isEigen ? '0 0 0 2px white inset' : '0 0 0 1px ' + C.grijs200,
          transition:'all .15s ease',
          flexShrink:0,
          display:'flex',
          alignItems:'center',
          justifyContent:'center',
          padding:0,
          fontSize:11,
          fontWeight:700,
          color:'#fff',
          textShadow:'0 1px 2px rgba(0,0,0,0.6)',
          lineHeight:1,
        }}
      >
        {!isEigen && '+'}
      </button>
      <input
        ref={inputRef}
        type="color"
        value={waarde}
        onChange={e => setWaarde(e.target.value)}
        style={{ position:'absolute', opacity:0, pointerEvents:'none', width:0, height:0 }}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPLATE 1 — DONKER & KRACHTIG
// ═══════════════════════════════════════════════════════════════════════════════
function TemplateDonker({ cv, zichtbaar, sectieVolgorde, eigenSecties, accent, balk, fotoGrootte = 100 }) {
  const ac = accent;
  const bk = balk || '#1e1c17';
  const bkTekst = accentTekst(bk);
  const bkMuted = bkTekst === '#ffffff' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.7)';
  const acT = accentTekst(ac);

  function WerkInhoud({ w }) {
    const bullets = w.bullets ? w.bullets.split('\n').filter(b => b.trim()) : [];
    return (
      <div style={{ marginBottom:18 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', flexWrap:'wrap', gap:4 }}>
          <span style={{ fontWeight:700, fontSize:14, color:C.inkt }}>{w.functie}</span>
          {w.periode && <span style={{ fontSize:12, color:C.grijs400 }}>{w.periode}</span>}
        </div>
        {w.bedrijf && <div style={{ fontSize:13, color: ac, fontWeight:600, marginBottom:5 }}>{w.bedrijf}</div>}
        {w.intro && <p style={{ fontSize:13, color:C.grijs800, marginBottom:5, lineHeight:1.6 }}>{w.intro}</p>}
        {bullets.length > 0 && (
          <ul style={{ paddingLeft:16, margin:'4px 0 6px' }}>
            {bullets.map((b,i) => <li key={i} style={{ fontSize:13, color:C.grijs800, marginBottom:3, lineHeight:1.55 }}>{b}</li>)}
          </ul>
        )}
        {w.resultaten && <p style={{ fontSize:12, color:C.grijs600, fontStyle:'italic', marginTop:4 }}>{w.resultaten}</p>}
      </div>
    );
  }

  function SectieKop({ label }) {
    return <div style={{ fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color: ac, borderBottom:`2px solid ${ac}`, paddingBottom:6, marginBottom:14 }}>{label}</div>;
  }

  function renderSectie(sid) {
    if (!zichtbaar[sid] && sid !== 'werkervaring') return null;
    if (sid === 'werkervaring' && zichtbaar.werkervaring && cv.werkervaring.some(w => w.bedrijf||w.functie)) return (
      <div key={sid} style={{ marginBottom:24 }}>
        <SectieKop label="Werkervaring" />
        {cv.werkervaring.map(w => (w.bedrijf||w.functie) ? <WerkInhoud key={w.id} w={w} /> : null)}
      </div>
    );
    if (sid === 'opleiding' && zichtbaar.opleiding && cv.opleiding.some(o => o.instelling||o.richting)) return (
      <div key={sid} style={{ marginBottom:24 }}>
        <SectieKop label="Opleiding" />
        {cv.opleiding.map(o => (o.instelling||o.richting) ? (
          <div key={o.id} style={{ marginBottom:12 }}>
            <div style={{ fontWeight:600, fontSize:14 }}>{o.richting}</div>
            {o.instelling && <div style={{ fontSize:13, color:C.grijs600 }}>{o.instelling}</div>}
            {o.periode && <div style={{ fontSize:12, color:C.grijs400 }}>{o.periode}</div>}
          </div>
        ) : null)}
      </div>
    );
    if (sid === 'vaardigheden' && zichtbaar.vaardigheden && cv.vaardigheden) return (
      <div key={sid} style={{ marginBottom:24 }}>
        <SectieKop label="Vaardigheden" />
        <div style={{ display:'flex', flexWrap:'wrap', gap:'6px 6px' }}>
          {cv.vaardigheden.split(',').map((v,i) => v.trim() ? (
            <span key={i} style={{ background: ac, color: acT, borderRadius:20, padding:'3px 11px', fontSize:12, fontWeight:600 }}>{v.trim()}</span>
          ) : null)}
        </div>
      </div>
    );
    if (sid === 'talen' && zichtbaar.talen && cv.talen.some(t => t.taal)) return (
      <div key={sid} style={{ marginBottom:24 }}>
        <SectieKop label="Talen" />
        <div style={{ display:'flex', flexWrap:'wrap', gap:'4px 20px' }}>
          {cv.talen.filter(t => t.taal).map(t => <span key={t.id} style={{ fontSize:13 }}><b>{t.taal}</b> — {t.niveau}</span>)}
        </div>
      </div>
    );
    if (sid === 'certificaten' && zichtbaar.certificaten && cv.certificaten.some(c => c.naam)) return (
      <div key={sid} style={{ marginBottom:24 }}>
        <SectieKop label="Certificaten" />
        {cv.certificaten.filter(c => c.naam).map(c => <div key={c.id} style={{ fontSize:13, marginBottom:4 }}>{c.naam}{c.jaar ? ` (${c.jaar})` : ''}</div>)}
      </div>
    );
    if (sid === 'vrijwilligerswerk' && zichtbaar.vrijwilligerswerk && cv.vrijwilligerswerk.some(v => v.organisatie||v.rol)) return (
      <div key={sid} style={{ marginBottom:24 }}>
        <SectieKop label="Vrijwilligerswerk" />
        {cv.vrijwilligerswerk.map(v => (v.organisatie||v.rol) ? (
          <div key={v.id} style={{ marginBottom:10 }}>
            <div style={{ fontWeight:600, fontSize:14 }}>{v.rol}</div>
            {v.organisatie && <div style={{ fontSize:13, color:C.grijs600 }}>{v.organisatie}</div>}
            {v.periode && <div style={{ fontSize:12, color:C.grijs400 }}>{v.periode}</div>}
          </div>
        ) : null)}
      </div>
    );
    const eigen = eigenSecties.find(e => e.id === sid);
    if (eigen && zichtbaar[sid] && eigen.tekst) return (
      <div key={sid} style={{ marginBottom:24 }}>
        <SectieKop label={eigen.titel || 'Eigen sectie'} />
        <p style={{ fontSize:13, color:C.grijs800, lineHeight:1.65, whiteSpace:'pre-wrap' }}>{eigen.tekst}</p>
      </div>
    );
    return null;
  }

  return (
    <div style={{ fontFamily:"'DM Sans', sans-serif", background:C.wit, minHeight:900 }}>
      {/* Header */}
      <div style={{ background: bk, padding:'32px 40px', display:'flex', alignItems:'center', gap:22, WebkitPrintColorAdjust:'exact', printColorAdjust:'exact' }}>
        {cv.foto && <img src={cv.foto} alt="" style={{ width:fotoGrootte, height:fotoGrootte, borderRadius:'50%', objectFit:'cover', border:`3px solid ${ac}`, flexShrink:0 }} />}
        <div>
          <div style={{ fontFamily:"'Lora', serif", fontSize:28, fontWeight:600, color: bkTekst, lineHeight:1.2, marginBottom:4 }}>{cv.naam || 'Jouw naam'}</div>
          {cv.functietitel && <div style={{ fontSize:15, color: ac, fontWeight:600, marginBottom:6 }}>{cv.functietitel}</div>}
          <div style={{ fontSize:12, color: bkMuted, display:'flex', flexWrap:'wrap', gap:'3px 14px' }}>
            {cv.email && <span>{cv.email}</span>}
            {cv.telefoon && <span>{cv.telefoon}</span>}
            {cv.locatie && <span>{cv.locatie}</span>}
            {cv.linkedin && <span>{cv.linkedin}</span>}
            {cv.website && <span>{cv.website}</span>}
          </div>
        </div>
      </div>

      {/* Wit body */}
      <div style={{ padding:'32px 40px' }}>
        {cv.samenvatting && (
          <div style={{ marginBottom:28, paddingBottom:20, borderBottom:`1px solid ${C.grijs100}` }}>
            <SectieKop label="Profiel" />
            <p style={{ fontSize:14, color:C.grijs800, lineHeight:1.75 }}>{cv.samenvatting}</p>
          </div>
        )}
        {sectieVolgorde.map(sid => renderSectie(sid))}
      </div>

      {/* Signatuur */}
      <div style={{ padding:'0 40px 20px', display:'flex', alignItems:'center', gap:5, opacity:.3 }}>
        <img src={logo} alt="" style={{ width:12, height:12 }} />
        <span style={{ fontSize:10 }}>Gemaakt met Tulpiaan CV Builder</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPLATE 2 — GESPLITST & ZAKELIJK
// ═══════════════════════════════════════════════════════════════════════════════
function TemplateGesplitst({ cv, zichtbaar, sectieVolgorde, eigenSecties, accent, balk, fotoGrootte = 100 }) {
  const ac = accent;
  const bk = balk || '#3a6090';

  const SIDEBAR_IDS = ['vaardigheden','talen','certificaten','opleiding'];
  const HOOFD_IDS   = ['werkervaring','vrijwilligerswerk'];

  const bkTekst = accentTekst(bk);
  const bkMuted = bkTekst === '#ffffff' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.7)';
  const bkSub   = bkTekst === '#ffffff' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.7)';

  function ZijSectieKop({ label }) {
    return <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.12em', color: bkMuted, marginTop:22, marginBottom:8, borderBottom:'1px solid rgba(128,128,128,0.25)', paddingBottom:5 }}>{label}</div>;
  }
  function HoofdSectieKop({ label }) {
    return <div style={{ fontFamily:"'Lora', serif", fontSize:16, fontWeight:600, color: ac, borderBottom:`2px solid ${ac}`, paddingBottom:5, marginBottom:14 }}>{label}</div>;
  }

  function WerkInhoud({ w }) {
    const bullets = w.bullets ? w.bullets.split('\n').filter(b => b.trim()) : [];
    return (
      <div style={{ marginBottom:18 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', flexWrap:'wrap' }}>
          <span style={{ fontWeight:700, fontSize:14, color:C.inkt }}>{w.functie}</span>
          {w.periode && <span style={{ fontSize:12, color:C.grijs400 }}>{w.periode}</span>}
        </div>
        {w.bedrijf && <div style={{ fontSize:13, color: ac, fontWeight:500, marginBottom:5 }}>{w.bedrijf}</div>}
        {w.intro && <p style={{ fontSize:13, color:C.grijs800, marginBottom:5, lineHeight:1.6 }}>{w.intro}</p>}
        {bullets.length > 0 && (
          <ul style={{ paddingLeft:16, margin:'4px 0 4px' }}>
            {bullets.map((b,i) => <li key={i} style={{ fontSize:13, color:C.grijs800, marginBottom:3, lineHeight:1.55 }}>{b}</li>)}
          </ul>
        )}
        {w.resultaten && <p style={{ fontSize:12, color:C.grijs600, fontStyle:'italic', marginTop:4 }}>{w.resultaten}</p>}
      </div>
    );
  }

  return (
    <div style={{ display:'flex', alignItems:'stretch', minHeight:900, fontFamily:"'DM Sans', sans-serif" }}>
      {/* Linkse zijbalk */}
      <div style={{ width:220, flexShrink:0, background: bk, padding:'36px 20px', display:'flex', flexDirection:'column', WebkitPrintColorAdjust:'exact', printColorAdjust:'exact' }}>
        {cv.foto && <img src={cv.foto} alt="" style={{ width:fotoGrootte, height:fotoGrootte, borderRadius:'50%', objectFit:'cover', marginBottom:14, border:'2px solid rgba(255,255,255,0.3)' }} />}
        <div style={{ fontFamily:"'Lora', serif", fontSize:20, fontWeight:600, color: bkTekst, lineHeight:1.3, marginBottom:3 }}>{cv.naam || 'Jouw naam'}</div>
        {cv.functietitel && <div style={{ fontSize:12, color: bkMuted, marginBottom:10 }}>{cv.functietitel}</div>}
        <ZijSectieKop label="Contact" />
        {[cv.email, cv.telefoon, cv.locatie, cv.linkedin, cv.website].filter(Boolean).map((v,i) => (
          <div key={i} style={{ fontSize:11, color: bkSub, marginBottom:5, wordBreak:'break-word' }}>{v}</div>
        ))}
        {sectieVolgorde.filter(s => SIDEBAR_IDS.includes(s)).map(sid => {
          if (!zichtbaar[sid]) return null;
          if (sid === 'vaardigheden' && cv.vaardigheden) return (
            <div key={sid}>
              <ZijSectieKop label="Vaardigheden" />
              {cv.vaardigheden.split(',').map((v,i) => v.trim() ? <div key={i} style={{ fontSize:11.5, color: bkSub, marginBottom:5 }}>• {v.trim()}</div> : null)}
            </div>
          );
          if (sid === 'talen' && cv.talen.some(t => t.taal)) return (
            <div key={sid}>
              <ZijSectieKop label="Talen" />
              {cv.talen.filter(t => t.taal).map(t => <div key={t.id} style={{ fontSize:11.5, color: bkSub, marginBottom:5 }}>{t.taal} — {t.niveau}</div>)}
            </div>
          );
          if (sid === 'certificaten' && cv.certificaten.some(c => c.naam)) return (
            <div key={sid}>
              <ZijSectieKop label="Certificaten" />
              {cv.certificaten.filter(c => c.naam).map(c => <div key={c.id} style={{ fontSize:11.5, color: bkSub, marginBottom:5 }}>{c.naam}{c.jaar ? ` (${c.jaar})` : ''}</div>)}
            </div>
          );
          if (sid === 'opleiding' && cv.opleiding.some(o => o.instelling||o.richting)) return (
            <div key={sid}>
              <ZijSectieKop label="Opleiding" />
              {cv.opleiding.map(o => (o.instelling||o.richting) ? (
                <div key={o.id} style={{ marginBottom:8 }}>
                  <div style={{ fontSize:12, fontWeight:600, color: bkTekst }}>{o.richting}</div>
                  {o.instelling && <div style={{ fontSize:11, color: bkMuted }}>{o.instelling}</div>}
                  {o.periode    && <div style={{ fontSize:11, color: bkMuted, opacity:.8 }}>{o.periode}</div>}
                </div>
              ) : null)}
            </div>
          );
          return null;
        })}
        <div style={{ marginTop:'auto', paddingTop:30, display:'flex', alignItems:'center', gap:5, opacity:.35 }}>
          <img src={logo} alt="" style={{ width:11, height:11, filter: bkTekst === '#ffffff' ? 'invert(1)' : 'none' }} />
          <span style={{ fontSize:9, color: bkTekst }}>Tulpiaan CV Builder</span>
        </div>
      </div>

      {/* Rechterkant */}
      <div style={{ flex:1, padding:'36px 32px', background:C.wit }}>
        {cv.samenvatting && (
          <div style={{ marginBottom:26 }}>
            <HoofdSectieKop label="Profiel" />
            <p style={{ fontSize:14, color:C.grijs800, lineHeight:1.75 }}>{cv.samenvatting}</p>
          </div>
        )}
        {sectieVolgorde.filter(s => HOOFD_IDS.includes(s) || eigenSecties.some(e => e.id === s)).map(sid => {
          if (!zichtbaar[sid]) return null;
          if (sid === 'werkervaring' && cv.werkervaring.some(w => w.bedrijf||w.functie)) return (
            <div key={sid} style={{ marginBottom:26 }}>
              <HoofdSectieKop label="Werkervaring" />
              {cv.werkervaring.map(w => (w.bedrijf||w.functie) ? <WerkInhoud key={w.id} w={w} /> : null)}
            </div>
          );
          if (sid === 'vrijwilligerswerk' && cv.vrijwilligerswerk.some(v => v.organisatie||v.rol)) return (
            <div key={sid} style={{ marginBottom:26 }}>
              <HoofdSectieKop label="Vrijwilligerswerk" />
              {cv.vrijwilligerswerk.map(v => (v.organisatie||v.rol) ? (
                <div key={v.id} style={{ marginBottom:10 }}>
                  <div style={{ fontWeight:600, fontSize:14 }}>{v.rol}</div>
                  {v.organisatie && <div style={{ fontSize:13, color:C.grijs600 }}>{v.organisatie}</div>}
                  {v.periode && <div style={{ fontSize:12, color:C.grijs400 }}>{v.periode}</div>}
                </div>
              ) : null)}
            </div>
          );
          const eigen = eigenSecties.find(e => e.id === sid);
          if (eigen && eigen.tekst) return (
            <div key={sid} style={{ marginBottom:26 }}>
              <HoofdSectieKop label={eigen.titel || 'Eigen sectie'} />
              <p style={{ fontSize:13, color:C.grijs800, lineHeight:1.65, whiteSpace:'pre-wrap' }}>{eigen.tekst}</p>
            </div>
          );
          return null;
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPLATE 3 — TIJDLIJN & STRAK
// ═══════════════════════════════════════════════════════════════════════════════
function TemplateTijdlijn({ cv, zichtbaar, sectieVolgorde, eigenSecties, accent, balk, fotoGrootte = 100 }) {
  const ac = accent;
  const bk = balk || '#1e1c17';
  const bkTekst = accentTekst(bk);
  const bkMuted = bkTekst === '#ffffff' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.7)';

  function SectieKop({ label }) {
    return <div style={{ fontSize:13, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:C.inkt, marginBottom:14, marginTop:24, paddingBottom:6, borderBottom:`1px solid ${C.grijs100}` }}>{label}</div>;
  }

  function renderSectie(sid) {
    if (!zichtbaar[sid]) return null;

    if (sid === 'werkervaring' && cv.werkervaring.some(w => w.bedrijf||w.functie)) {
      const items = cv.werkervaring.filter(w => w.bedrijf||w.functie);
      return (
        <div key={sid}>
          <SectieKop label="Werkervaring" />
          <div style={{ position:'relative', paddingLeft:28 }}>
            {/* Verticale lijn */}
            <div style={{ position:'absolute', left:6, top:8, bottom:8, width:2, background: ac }} />
            {items.map(w => {
              const bullets = w.bullets ? w.bullets.split('\n').filter(b => b.trim()) : [];
              return (
                <div key={w.id} style={{ position:'relative', marginBottom:22 }}>
                  {/* Dot */}
                  <div style={{ position:'absolute', left:-24, top:5, width:12, height:12, borderRadius:'50%', background: ac, border:`2px solid ${C.wit}`, boxShadow:`0 0 0 1.5px ${ac}` }} />
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', flexWrap:'wrap' }}>
                    <span style={{ fontWeight:700, fontSize:14, color:C.inkt }}>{w.bedrijf}</span>
                    {w.periode && <span style={{ fontSize:12, color:C.grijs400 }}>{w.periode}</span>}
                  </div>
                  {w.functie && <div style={{ fontSize:13, color: ac, fontWeight:600, marginBottom:5 }}>{w.functie}</div>}
                  {w.intro && <p style={{ fontSize:13, color:C.grijs800, lineHeight:1.6, marginBottom:5 }}>{w.intro}</p>}
                  {bullets.length > 0 && (
                    <ul style={{ paddingLeft:16, margin:'3px 0 4px' }}>
                      {bullets.map((b,i) => <li key={i} style={{ fontSize:13, color:C.grijs800, marginBottom:3, lineHeight:1.55 }}>{b}</li>)}
                    </ul>
                  )}
                  {w.resultaten && <p style={{ fontSize:12, color:C.grijs600, fontStyle:'italic', marginTop:3 }}>{w.resultaten}</p>}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    if (sid === 'opleiding' && zichtbaar.opleiding && cv.opleiding.some(o => o.instelling||o.richting)) return (
      <div key={sid}>
        <SectieKop label="Opleiding" />
        {cv.opleiding.map(o => (o.instelling||o.richting) ? (
          <div key={o.id} style={{ marginBottom:10 }}>
            <div style={{ fontWeight:600, fontSize:14 }}>{o.richting}</div>
            {o.instelling && <div style={{ fontSize:13, color:C.grijs600 }}>{o.instelling}</div>}
            {o.periode && <div style={{ fontSize:12, color:C.grijs400 }}>{o.periode}</div>}
          </div>
        ) : null)}
      </div>
    );
    if (sid === 'vaardigheden' && zichtbaar.vaardigheden && cv.vaardigheden) return (
      <div key={sid}>
        <SectieKop label="Vaardigheden" />
        <div style={{ display:'flex', flexWrap:'wrap', gap:'5px 6px' }}>
          {cv.vaardigheden.split(',').map((v,i) => v.trim() ? (
            <span key={i} style={{ border:`1.5px solid ${ac}`, color: ac, borderRadius:20, padding:'3px 11px', fontSize:12, fontWeight:500, background:'transparent' }}>{v.trim()}</span>
          ) : null)}
        </div>
      </div>
    );
    if (sid === 'talen' && zichtbaar.talen && cv.talen.some(t => t.taal)) return (
      <div key={sid}>
        <SectieKop label="Talen" />
        <div style={{ display:'flex', flexWrap:'wrap', gap:'4px 20px' }}>
          {cv.talen.filter(t => t.taal).map(t => <span key={t.id} style={{ fontSize:13 }}><b>{t.taal}</b> — {t.niveau}</span>)}
        </div>
      </div>
    );
    if (sid === 'certificaten' && zichtbaar.certificaten && cv.certificaten.some(c => c.naam)) return (
      <div key={sid}>
        <SectieKop label="Certificaten" />
        {cv.certificaten.filter(c => c.naam).map(c => <div key={c.id} style={{ fontSize:13, marginBottom:4 }}>{c.naam}{c.jaar ? ` (${c.jaar})` : ''}</div>)}
      </div>
    );
    if (sid === 'vrijwilligerswerk' && zichtbaar.vrijwilligerswerk && cv.vrijwilligerswerk.some(v => v.organisatie||v.rol)) return (
      <div key={sid}>
        <SectieKop label="Vrijwilligerswerk" />
        {cv.vrijwilligerswerk.map(v => (v.organisatie||v.rol) ? (
          <div key={v.id} style={{ marginBottom:10 }}>
            <div style={{ fontWeight:600, fontSize:14 }}>{v.rol}</div>
            {v.organisatie && <div style={{ fontSize:13, color:C.grijs600 }}>{v.organisatie}</div>}
            {v.periode && <div style={{ fontSize:12, color:C.grijs400 }}>{v.periode}</div>}
          </div>
        ) : null)}
      </div>
    );
    const eigen = eigenSecties.find(e => e.id === sid);
    if (eigen && zichtbaar[sid] && eigen.tekst) return (
      <div key={sid}>
        <SectieKop label={eigen.titel || 'Eigen sectie'} />
        <p style={{ fontSize:13, color:C.grijs800, lineHeight:1.65, whiteSpace:'pre-wrap' }}>{eigen.tekst}</p>
      </div>
    );
    return null;
  }

  return (
    <div style={{ fontFamily:"'DM Sans', sans-serif", background:C.wit, minHeight:900 }}>
      {/* Header */}
      <div style={{ background: bk, padding:'28px 40px', display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12, WebkitPrintColorAdjust:'exact', printColorAdjust:'exact' }}>
        <div>
          <div style={{ fontFamily:"'Lora', serif", fontSize:30, fontWeight:600, color: bkTekst, lineHeight:1.15, marginBottom:4 }}>{cv.naam || 'Jouw naam'}</div>
          {cv.functietitel && <div style={{ fontSize:14, color: ac, fontWeight:600 }}>{cv.functietitel}</div>}
        </div>
        <div style={{ display:'flex', alignItems:'flex-start', gap:16 }}>
          <div style={{ fontSize:12, color: bkMuted, textAlign:'right', lineHeight:1.8 }}>
            {cv.email && <div>{cv.email}</div>}
            {cv.telefoon && <div>{cv.telefoon}</div>}
            {cv.locatie && <div>{cv.locatie}</div>}
            {cv.linkedin && <div>{cv.linkedin}</div>}
            {cv.website && <div>{cv.website}</div>}
          </div>
          {cv.foto && <img src={cv.foto} alt="" style={{ width:fotoGrootte, height:fotoGrootte, borderRadius:'50%', objectFit:'cover', border:`2px solid ${ac}`, flexShrink:0 }} />}
        </div>
      </div>

      {/* Wit body */}
      <div style={{ padding:'28px 40px 40px' }}>
        {cv.samenvatting && (
          <div style={{ marginBottom:4 }}>
            <SectieKop label="Profiel" />
            <p style={{ fontSize:14, color:C.grijs800, lineHeight:1.75 }}>{cv.samenvatting}</p>
          </div>
        )}
        {sectieVolgorde.map(sid => renderSectie(sid))}
      </div>

      <div style={{ padding:'0 40px 20px', display:'flex', alignItems:'center', gap:5, opacity:.3 }}>
        <img src={logo} alt="" style={{ width:12, height:12 }} />
        <span style={{ fontSize:10 }}>Gemaakt met Tulpiaan CV Builder</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PDF EXPORT — zelfstandig print-HTML-document
// ═══════════════════════════════════════════════════════════════════════════════
function exporteerPDF(cv, zichtbaar, sectieVolgorde, eigenSecties, template, accent, balk, logoUrl, fotoGrootte = 100) {
  const esc = s => String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  const hRgb = hex => ({ r:parseInt(hex.slice(1,3),16), g:parseInt(hex.slice(3,5),16), b:parseInt(hex.slice(5,7),16) });
  const acTekst = hex => { const {r,g,b} = hRgb(hex); return (0.299*r+0.587*g+0.114*b)>145 ? '#1e1c17' : '#ffffff'; };

  const ac  = accent;
  const bk  = balk;
  const bkT = acTekst(bk);
  const bkM = bkT==='#ffffff' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.7)';
  const bkS = bkT==='#ffffff' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.7)';
  const acT = acTekst(ac);
  const contact = [cv.email, cv.telefoon, cv.locatie, cv.linkedin, cv.website].filter(Boolean);

  const foto = (border) => cv.foto ? `<img src="${cv.foto}" style="width:${fotoGrootte}px;height:${fotoGrootte}px;border-radius:50%;object-fit:cover;border:${border};display:block;">` : '';
  const logoTag = (extra='') => logoUrl ? `<div style="display:flex;align-items:center;gap:5px;opacity:.3;${extra}"><img src="${logoUrl}" style="width:12px;height:12px;"><span style="font-size:10px;">Gemaakt met Tulpiaan CV Builder</span></div>` : '';

  const werkHtml = (w, ac) => {
    const bullets = w.bullets ? w.bullets.split('\n').filter(b=>b.trim()) : [];
    return `<div style="margin-bottom:18px;">
      <div style="display:flex;justify-content:space-between;align-items:baseline;flex-wrap:wrap;gap:4px;">
        <span style="font-weight:700;font-size:14px;color:#1e1c17;">${esc(w.functie)}</span>
        ${w.periode ? `<span style="font-size:12px;color:#9e9b93;">${esc(w.periode)}</span>` : ''}
      </div>
      ${w.bedrijf ? `<div style="font-size:13px;color:${ac};font-weight:600;margin-bottom:5px;">${esc(w.bedrijf)}</div>` : ''}
      ${w.intro ? `<p style="font-size:13px;color:#3a3830;margin-bottom:5px;line-height:1.6;">${esc(w.intro)}</p>` : ''}
      ${bullets.length ? `<ul style="padding-left:16px;margin:4px 0 6px;">${bullets.map(b=>`<li style="font-size:13px;color:#3a3830;margin-bottom:3px;">${esc(b)}</li>`).join('')}</ul>` : ''}
      ${w.resultaten ? `<p style="font-size:12px;color:#6b6860;font-style:italic;margin-top:4px;">${esc(w.resultaten)}</p>` : ''}
    </div>`;
  };

  let body = '';

  // ── Template Donker ────────────────────────────────────────────────────────
  if (template === 'donker') {
    const kop = label => `<div style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.1em;color:${ac};border-bottom:2px solid ${ac};padding-bottom:6px;margin-bottom:14px;">${esc(label)}</div>`;
    const renderSec = sid => {
      if (!zichtbaar[sid]) return '';
      if (sid==='werkervaring' && cv.werkervaring.some(w=>w.bedrijf||w.functie)) return `<div style="margin-bottom:24px;">${kop('Werkervaring')}${cv.werkervaring.filter(w=>w.bedrijf||w.functie).map(w=>werkHtml(w,ac)).join('')}</div>`;
      if (sid==='opleiding' && cv.opleiding.some(o=>o.instelling||o.richting)) return `<div style="margin-bottom:24px;">${kop('Opleiding')}${cv.opleiding.filter(o=>o.instelling||o.richting).map(o=>`<div style="margin-bottom:12px;"><div style="font-weight:600;font-size:14px;">${esc(o.richting)}</div>${o.instelling?`<div style="font-size:13px;color:#6b6860;">${esc(o.instelling)}</div>`:''} ${o.periode?`<div style="font-size:12px;color:#9e9b93;">${esc(o.periode)}</div>`:''}</div>`).join('')}</div>`;
      if (sid==='vaardigheden' && cv.vaardigheden) return `<div style="margin-bottom:24px;">${kop('Vaardigheden')}<div style="display:flex;flex-wrap:wrap;gap:6px;">${cv.vaardigheden.split(',').filter(v=>v.trim()).map(v=>`<span style="background:${ac};color:${acT};border-radius:20px;padding:3px 11px;font-size:12px;font-weight:600;">${esc(v.trim())}</span>`).join('')}</div></div>`;
      if (sid==='talen' && cv.talen.some(t=>t.taal)) return `<div style="margin-bottom:24px;">${kop('Talen')}<div style="display:flex;flex-wrap:wrap;gap:4px 20px;">${cv.talen.filter(t=>t.taal).map(t=>`<span style="font-size:13px;"><b>${esc(t.taal)}</b> — ${esc(t.niveau)}</span>`).join('')}</div></div>`;
      if (sid==='certificaten' && cv.certificaten.some(c=>c.naam)) return `<div style="margin-bottom:24px;">${kop('Certificaten')}${cv.certificaten.filter(c=>c.naam).map(c=>`<div style="font-size:13px;margin-bottom:4px;">${esc(c.naam)}${c.jaar?` (${esc(c.jaar)})`:''}	</div>`).join('')}</div>`;
      if (sid==='vrijwilligerswerk' && cv.vrijwilligerswerk.some(v=>v.organisatie||v.rol)) return `<div style="margin-bottom:24px;">${kop('Vrijwilligerswerk')}${cv.vrijwilligerswerk.filter(v=>v.organisatie||v.rol).map(v=>`<div style="margin-bottom:10px;"><div style="font-weight:600;font-size:14px;">${esc(v.rol)}</div>${v.organisatie?`<div style="font-size:13px;color:#6b6860;">${esc(v.organisatie)}</div>`:''} ${v.periode?`<div style="font-size:12px;color:#9e9b93;">${esc(v.periode)}</div>`:''}</div>`).join('')}</div>`;
      const e = eigenSecties.find(e=>e.id===sid);
      if (e && e.tekst) return `<div style="margin-bottom:24px;">${kop(e.titel||'Eigen sectie')}<p style="font-size:13px;color:#3a3830;line-height:1.65;white-space:pre-wrap;">${esc(e.tekst)}</p></div>`;
      return '';
    };
    body = `
      <div style="background:${bk};width:100%;padding:32px 30px;box-sizing:border-box;display:flex;align-items:center;gap:22px;-webkit-print-color-adjust:exact;print-color-adjust:exact;">
        ${foto(`3px solid ${ac}`)}
        <div>
          <div style="font-family:'Lora',serif;font-size:28px;font-weight:600;color:${bkT};line-height:1.2;margin-bottom:4px;">${esc(cv.naam||'Jouw naam')}</div>
          ${cv.functietitel?`<div style="font-size:15px;color:${ac};font-weight:600;margin-bottom:6px;">${esc(cv.functietitel)}</div>`:''}
          <div style="font-size:12px;color:${bkM};display:flex;flex-wrap:wrap;gap:3px 14px;">${contact.map(v=>`<span>${esc(v)}</span>`).join('')}</div>
        </div>
      </div>
      <div style="padding:24px 30px;">
        ${cv.samenvatting?`<div style="margin-bottom:28px;padding-bottom:20px;border-bottom:1px solid #edecea;">${kop('Profiel')}<p style="font-size:14px;color:#3a3830;line-height:1.75;">${esc(cv.samenvatting)}</p></div>`:''}
        ${sectieVolgorde.map(renderSec).join('')}
      </div>
      ${logoTag('padding:0 30px 20px;')}`;
  }

  // ── Template Gesplitst ─────────────────────────────────────────────────────
  else if (template === 'gesplitst') {
    const SIDEBAR_IDS = ['vaardigheden','talen','certificaten','opleiding'];
    const HOOFD_IDS   = ['werkervaring','vrijwilligerswerk'];
    const zijKop = label => `<div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.12em;color:${bkM};margin-top:22px;margin-bottom:8px;border-bottom:1px solid rgba(128,128,128,0.25);padding-bottom:5px;">${esc(label)}</div>`;
    const hoofdKop = label => `<div style="font-family:'Lora',serif;font-size:16px;font-weight:600;color:${ac};border-bottom:2px solid ${ac};padding-bottom:5px;margin-bottom:14px;">${esc(label)}</div>`;

    const sidebarSecs = sectieVolgorde.filter(s=>SIDEBAR_IDS.includes(s)).map(sid => {
      if (!zichtbaar[sid]) return '';
      if (sid==='vaardigheden' && cv.vaardigheden) return `${zijKop('Vaardigheden')}${cv.vaardigheden.split(',').filter(v=>v.trim()).map(v=>`<div style="font-size:11.5px;color:${bkS};margin-bottom:5px;">&#x2022; ${esc(v.trim())}</div>`).join('')}`;
      if (sid==='talen' && cv.talen.some(t=>t.taal)) return `${zijKop('Talen')}${cv.talen.filter(t=>t.taal).map(t=>`<div style="font-size:11.5px;color:${bkS};margin-bottom:5px;">${esc(t.taal)} &#x2014; ${esc(t.niveau)}</div>`).join('')}`;
      if (sid==='certificaten' && cv.certificaten.some(c=>c.naam)) return `${zijKop('Certificaten')}${cv.certificaten.filter(c=>c.naam).map(c=>`<div style="font-size:11.5px;color:${bkS};margin-bottom:5px;">${esc(c.naam)}${c.jaar?` (${esc(c.jaar)})`:''}	</div>`).join('')}`;
      if (sid==='opleiding' && cv.opleiding.some(o=>o.instelling||o.richting)) return `${zijKop('Opleiding')}${cv.opleiding.filter(o=>o.instelling||o.richting).map(o=>`<div style="margin-bottom:8px;"><div style="font-size:12px;font-weight:600;color:${bkT};">${esc(o.richting)}</div>${o.instelling?`<div style="font-size:11px;color:${bkM};">${esc(o.instelling)}</div>`:''} ${o.periode?`<div style="font-size:11px;color:${bkM};opacity:.8;">${esc(o.periode)}</div>`:''}</div>`).join('')}`;
      return '';
    }).join('');

    const hoofdSecs = sectieVolgorde.filter(s=>HOOFD_IDS.includes(s)||eigenSecties.some(e=>e.id===s)).map(sid => {
      if (!zichtbaar[sid]) return '';
      if (sid==='werkervaring' && cv.werkervaring.some(w=>w.bedrijf||w.functie)) return `<div style="margin-bottom:26px;">${hoofdKop('Werkervaring')}${cv.werkervaring.filter(w=>w.bedrijf||w.functie).map(w=>werkHtml(w,ac)).join('')}</div>`;
      if (sid==='vrijwilligerswerk' && cv.vrijwilligerswerk.some(v=>v.organisatie||v.rol)) return `<div style="margin-bottom:26px;">${hoofdKop('Vrijwilligerswerk')}${cv.vrijwilligerswerk.filter(v=>v.organisatie||v.rol).map(v=>`<div style="margin-bottom:10px;"><div style="font-weight:600;font-size:14px;">${esc(v.rol)}</div>${v.organisatie?`<div style="font-size:13px;color:#6b6860;">${esc(v.organisatie)}</div>`:''} ${v.periode?`<div style="font-size:12px;color:#9e9b93;">${esc(v.periode)}</div>`:''}</div>`).join('')}</div>`;
      const e = eigenSecties.find(e=>e.id===sid);
      if (e && e.tekst) return `<div style="margin-bottom:26px;">${hoofdKop(e.titel||'Eigen sectie')}<p style="font-size:13px;color:#3a3830;line-height:1.65;white-space:pre-wrap;">${esc(e.tekst)}</p></div>`;
      return '';
    }).join('');

    body = `
      <div style="display:flex;width:100%;">
        <div style="width:220px;flex-shrink:0;padding:28px 20px;box-sizing:border-box;">
          ${cv.foto?`<img src="${cv.foto}" style="width:${fotoGrootte}px;height:${fotoGrootte}px;border-radius:50%;object-fit:cover;margin-bottom:14px;border:2px solid rgba(255,255,255,0.3);display:block;">`:''}
          <div style="font-family:'Lora',serif;font-size:20px;font-weight:600;color:${bkT};line-height:1.3;margin-bottom:3px;">${esc(cv.naam||'Jouw naam')}</div>
          ${cv.functietitel?`<div style="font-size:12px;color:${bkM};margin-bottom:10px;">${esc(cv.functietitel)}</div>`:''}
          ${zijKop('Contact')}
          ${contact.map(v=>`<div style="font-size:11px;color:${bkS};margin-bottom:5px;word-break:break-word;">${esc(v)}</div>`).join('')}
          ${sidebarSecs}
          ${logoUrl?`<div style="margin-top:30px;display:flex;align-items:center;gap:5px;opacity:.35;"><img src="${logoUrl}" style="width:11px;height:11px;${bkT==='#ffffff'?'filter:invert(1);':''}"><span style="font-size:9px;color:${bkT};">Tulpiaan CV Builder</span></div>`:''}
        </div>
        <div style="flex:1;padding:28px 24px;background:white;box-sizing:border-box;">
          ${cv.samenvatting?`<div style="margin-bottom:26px;">${hoofdKop('Profiel')}<p style="font-size:14px;color:#3a3830;line-height:1.75;">${esc(cv.samenvatting)}</p></div>`:''}
          ${hoofdSecs}
        </div>
      </div>`;
  }

  // ── Template Tijdlijn ──────────────────────────────────────────────────────
  else if (template === 'tijdlijn') {
    const kop = label => `<div style="font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.1em;color:#1e1c17;margin-bottom:14px;margin-top:24px;padding-bottom:6px;border-bottom:1px solid #edecea;">${esc(label)}</div>`;
    const renderSec = sid => {
      if (!zichtbaar[sid]) return '';
      if (sid==='werkervaring' && cv.werkervaring.some(w=>w.bedrijf||w.functie)) {
        const items = cv.werkervaring.filter(w=>w.bedrijf||w.functie);
        return `${kop('Werkervaring')}<div style="position:relative;padding-left:28px;">
          <div style="position:absolute;left:6px;top:8px;bottom:8px;width:2px;background:${ac};"></div>
          ${items.map(w=>{
            const bullets = w.bullets ? w.bullets.split('\n').filter(b=>b.trim()) : [];
            return `<div style="position:relative;margin-bottom:22px;">
              <div style="position:absolute;left:-24px;top:5px;width:12px;height:12px;border-radius:50%;background:${ac};border:2px solid white;box-shadow:0 0 0 1.5px ${ac};"></div>
              <div style="display:flex;justify-content:space-between;align-items:baseline;flex-wrap:wrap;">
                <span style="font-weight:700;font-size:14px;color:#1e1c17;">${esc(w.bedrijf)}</span>
                ${w.periode?`<span style="font-size:12px;color:#9e9b93;">${esc(w.periode)}</span>`:''}
              </div>
              ${w.functie?`<div style="font-size:13px;color:${ac};font-weight:600;margin-bottom:5px;">${esc(w.functie)}</div>`:''}
              ${w.intro?`<p style="font-size:13px;color:#3a3830;line-height:1.6;margin-bottom:5px;">${esc(w.intro)}</p>`:''}
              ${bullets.length?`<ul style="padding-left:16px;margin:3px 0 4px;">${bullets.map(b=>`<li style="font-size:13px;color:#3a3830;margin-bottom:3px;">${esc(b)}</li>`).join('')}</ul>`:''}
              ${w.resultaten?`<p style="font-size:12px;color:#6b6860;font-style:italic;margin-top:3px;">${esc(w.resultaten)}</p>`:''}
            </div>`;
          }).join('')}
        </div>`;
      }
      if (sid==='opleiding' && cv.opleiding.some(o=>o.instelling||o.richting)) return `${kop('Opleiding')}${cv.opleiding.filter(o=>o.instelling||o.richting).map(o=>`<div style="margin-bottom:10px;"><div style="font-weight:600;font-size:14px;">${esc(o.richting)}</div>${o.instelling?`<div style="font-size:13px;color:#6b6860;">${esc(o.instelling)}</div>`:''} ${o.periode?`<div style="font-size:12px;color:#9e9b93;">${esc(o.periode)}</div>`:''}</div>`).join('')}`;
      if (sid==='vaardigheden' && cv.vaardigheden) return `${kop('Vaardigheden')}<div style="display:flex;flex-wrap:wrap;gap:5px 6px;">${cv.vaardigheden.split(',').filter(v=>v.trim()).map(v=>`<span style="border:1.5px solid ${ac};color:${ac};border-radius:20px;padding:3px 11px;font-size:12px;font-weight:500;">${esc(v.trim())}</span>`).join('')}</div>`;
      if (sid==='talen' && cv.talen.some(t=>t.taal)) return `${kop('Talen')}<div style="display:flex;flex-wrap:wrap;gap:4px 20px;">${cv.talen.filter(t=>t.taal).map(t=>`<span style="font-size:13px;"><b>${esc(t.taal)}</b> &#x2014; ${esc(t.niveau)}</span>`).join('')}</div>`;
      if (sid==='certificaten' && cv.certificaten.some(c=>c.naam)) return `${kop('Certificaten')}${cv.certificaten.filter(c=>c.naam).map(c=>`<div style="font-size:13px;margin-bottom:4px;">${esc(c.naam)}${c.jaar?` (${esc(c.jaar)})`:''}	</div>`).join('')}`;
      if (sid==='vrijwilligerswerk' && cv.vrijwilligerswerk.some(v=>v.organisatie||v.rol)) return `${kop('Vrijwilligerswerk')}${cv.vrijwilligerswerk.filter(v=>v.organisatie||v.rol).map(v=>`<div style="margin-bottom:10px;"><div style="font-weight:600;font-size:14px;">${esc(v.rol)}</div>${v.organisatie?`<div style="font-size:13px;color:#6b6860;">${esc(v.organisatie)}</div>`:''} ${v.periode?`<div style="font-size:12px;color:#9e9b93;">${esc(v.periode)}</div>`:''}</div>`).join('')}`;
      const e = eigenSecties.find(e=>e.id===sid);
      if (e && e.tekst) return `${kop(e.titel||'Eigen sectie')}<p style="font-size:13px;color:#3a3830;line-height:1.65;white-space:pre-wrap;">${esc(e.tekst)}</p>`;
      return '';
    };
    body = `
      <div style="background:${bk};width:100%;padding:28px 30px;box-sizing:border-box;display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:12px;-webkit-print-color-adjust:exact;print-color-adjust:exact;">
        <div>
          <div style="font-family:'Lora',serif;font-size:30px;font-weight:600;color:${bkT};line-height:1.15;margin-bottom:4px;">${esc(cv.naam||'Jouw naam')}</div>
          ${cv.functietitel?`<div style="font-size:14px;color:${ac};font-weight:600;">${esc(cv.functietitel)}</div>`:''}
        </div>
        <div style="display:flex;align-items:flex-start;gap:16px;">
          <div style="font-size:12px;color:${bkM};text-align:right;line-height:1.8;">${contact.map(v=>`<div>${esc(v)}</div>`).join('')}</div>
          ${cv.foto?`<img src="${cv.foto}" style="width:${fotoGrootte}px;height:${fotoGrootte}px;border-radius:50%;object-fit:cover;border:2px solid ${ac};flex-shrink:0;">` : ''}
        </div>
      </div>
      <div style="padding:20px 30px 30px;">
        ${cv.samenvatting?`<div style="margin-bottom:24px;">${kop('Profiel')}<p style="font-size:14px;color:#3a3830;line-height:1.75;">${esc(cv.samenvatting)}</p></div>`:''}
        ${sectieVolgorde.map(renderSec).join('')}
      </div>
      ${logoTag('padding:0 30px 20px;')}`;
  }

  const bodyBg = template === 'gesplitst'
    ? `linear-gradient(to right, ${bk} 0px, ${bk} 220px, white 220px, white 100%)`
    : 'white';

  const html = `<!DOCTYPE html>
<html lang="nl">
<head>
  <meta charset="UTF-8">
  <title>CV &#x2014; ${esc(cv.naam||'Tulpiaan')}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html, body { margin: 0; padding: 0; }
    body { font-family: 'DM Sans', sans-serif; background: ${bodyBg}; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    ul { list-style: disc; }
    @page { size: A4; margin: 0; }
    .print-tip { display: block; background: #fffbe6; border-bottom: 1px solid #f0d080; padding: 10px 30px; font-size: 13px; color: #7a6020; font-family: 'DM Sans', sans-serif; }
    @media print {
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      html, body { margin: 0 !important; padding: 0 !important; background: ${bodyBg} !important; }
      .print-tip { display: none !important; }
    }
  </style>
</head>
<body>
  <div class="print-tip">&#9432; Tip: zet in je printinstellingen &#x27;Kopteksten en voetteksten&#x27; uit voor een schone PDF.</div>
  ${body}
  <script>
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function() { setTimeout(function() { window.print(); }, 300); });
    } else {
      window.addEventListener('load', function() { setTimeout(function() { window.print(); }, 700); });
    }
  </script>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) { alert('Sta pop-ups toe in je browser om de PDF te kunnen genereren.'); return; }
  win.document.write(html);
  win.document.close();
}

// ═══════════════════════════════════════════════════════════════════════════════
// WORD EXPORT
// ═══════════════════════════════════════════════════════════════════════════════
async function exporteerWord(cv, zichtbaar, sectieVolgorde, eigenSecties) {
  const kop = t => new Paragraph({
    text: t, heading: HeadingLevel.HEADING_2,
    border: { bottom: { color: '1e1c17', style: BorderStyle.SINGLE, size: 4, space: 4 } },
    spacing: { before: 300, after: 100 },
  });
  const tekst = (t, opts = {}) => new Paragraph({ children: [new TextRun({ text: t, size: 22, ...opts })], spacing: { after: 60 } });
  const bullet = t => new Paragraph({ children: [new TextRun({ text: `• ${t}`, size: 22 })], spacing: { after: 60 } });

  const ch = [
    new Paragraph({ children: [new TextRun({ text: cv.naam || 'CV', bold: true, size: 52, font: 'Georgia' })], alignment: AlignmentType.LEFT }),
    new Paragraph({ children: [new TextRun({ text: [cv.email, cv.telefoon, cv.locatie, cv.linkedin, cv.website].filter(Boolean).join('  ·  '), color: '6b6860', size: 20 })], spacing: { after: 200 } }),
  ];
  if (cv.samenvatting) { ch.push(kop('Profiel')); ch.push(tekst(cv.samenvatting)); }

  sectieVolgorde.forEach(sid => {
    if (!zichtbaar[sid]) return;
    if (sid === 'werkervaring' && cv.werkervaring.some(w => w.bedrijf||w.functie)) {
      ch.push(kop('Werkervaring'));
      cv.werkervaring.forEach(w => {
        if (!w.bedrijf && !w.functie) return;
        ch.push(new Paragraph({ children: [new TextRun({ text: [w.functie, w.bedrijf].filter(Boolean).join(' — '), bold: true, size: 22 })] }));
        if (w.periode) ch.push(tekst(w.periode, { color: '9e9b93', size: 20 }));
        if (w.intro) ch.push(tekst(w.intro));
        if (w.bullets) w.bullets.split('\n').filter(b => b.trim()).forEach(b => ch.push(bullet(b.trim())));
        if (w.resultaten) ch.push(tekst(w.resultaten, { italics: true, color: '6b6860' }));
        ch.push(new Paragraph({ text: '', spacing: { after: 120 } }));
      });
    } else if (sid === 'opleiding' && cv.opleiding.some(o => o.instelling||o.richting)) {
      ch.push(kop('Opleiding'));
      cv.opleiding.forEach(o => {
        if (!o.instelling && !o.richting) return;
        ch.push(new Paragraph({ children: [new TextRun({ text: [o.richting, o.instelling].filter(Boolean).join(' — '), bold: true, size: 22 })] }));
        if (o.periode) ch.push(tekst(o.periode, { color: '9e9b93', size: 20 }));
      });
    } else if (sid === 'vaardigheden' && cv.vaardigheden) {
      ch.push(kop('Vaardigheden')); ch.push(tekst(cv.vaardigheden));
    } else if (sid === 'talen' && cv.talen.some(t => t.taal)) {
      ch.push(kop('Talen'));
      cv.talen.filter(t => t.taal).forEach(t => ch.push(tekst(`${t.taal} — ${t.niveau}`)));
    } else if (sid === 'certificaten' && cv.certificaten.some(c => c.naam)) {
      ch.push(kop('Certificaten'));
      cv.certificaten.filter(c => c.naam).forEach(c => ch.push(tekst(c.jaar ? `${c.naam} (${c.jaar})` : c.naam)));
    } else if (sid === 'vrijwilligerswerk' && cv.vrijwilligerswerk.some(v => v.organisatie||v.rol)) {
      ch.push(kop('Vrijwilligerswerk'));
      cv.vrijwilligerswerk.forEach(v => {
        if (!v.organisatie && !v.rol) return;
        ch.push(new Paragraph({ children: [new TextRun({ text: [v.rol, v.organisatie].filter(Boolean).join(' — '), bold: true, size: 22 })] }));
        if (v.periode) ch.push(tekst(v.periode, { color: '9e9b93', size: 20 }));
      });
    } else {
      const eigen = eigenSecties.find(e => e.id === sid);
      if (eigen && eigen.tekst) { ch.push(kop(eigen.titel || 'Eigen sectie')); ch.push(tekst(eigen.tekst)); }
    }
  });
  ch.push(new Paragraph({ children: [new TextRun({ text: 'Gemaakt met Tulpiaan CV Builder', color: 'c0bdb5', size: 16 })], spacing: { before: 600 } }));
  const doc = new Document({ sections: [{ properties: { page: { margin: { top: 900, right: 900, bottom: 900, left: 900 } } }, children: ch }] });
  const blob = await Packer.toBlob(doc);
  saveAs(blob, `CV_${cv.naam || 'tulpiaan'}.docx`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// BEWAARCODE — UI-componenten
// ═══════════════════════════════════════════════════════════════════════════════

function ModalShell({ titel, onSluit, children, breedte = 520 }) {
  // Esc om te sluiten
  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onSluit(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onSluit]);

  return (
    <div onClick={onSluit}
      style={{ position:'fixed', inset:0, background:'rgba(20,18,12,.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16, animation:'fadeUp .2s ease' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background:C.papier, borderRadius:12, width:'100%', maxWidth:breedte, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,.3)', border:`1px solid ${C.grijs100}` }}>
        <div style={{ padding:'16px 20px', borderBottom:`1px solid ${C.grijs100}`, display:'flex', alignItems:'center', justifyContent:'space-between', background:C.wit, borderRadius:'12px 12px 0 0' }}>
          <span style={{ fontFamily:"'Lora', serif", fontSize:16, fontWeight:600, color:C.inkt }}>{titel}</span>
          <button onClick={onSluit}
            style={{ border:'none', background:'none', fontSize:20, color:C.grijs400, cursor:'pointer', lineHeight:1, padding:4 }}
            aria-label="Sluiten">×</button>
        </div>
        <div style={{ padding:'18px 20px 22px' }}>{children}</div>
      </div>
    </div>
  );
}

function BewaarcodeModal({ snapshot, cv, accent, onSluit, onOpgeslagen }) {
  const [code, setCode] = useState('');
  const [fout, setFout] = useState('');
  const [gekopieerd, setGekopieerd] = useState(false);

  // Code direct genereren bij openen — geen aparte "genereer"-knop nodig
  useEffect(() => {
    try { setCode(generateSaveCode(snapshot)); }
    catch (e) { setFout('Genereren mislukt: ' + e.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const codeLengte = code.length;
  const mailtoLimiet = 1900; // veilige marge onder 2000

  function downloadAlsBestand() {
    if (!code) return;
    const blob = new Blob([code], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, bestandsnaamCV(cv));
    onOpgeslagen?.();
  }

  function kopieerNaarKlembord() {
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      setGekopieerd(true);
      setTimeout(() => setGekopieerd(false), 3000);
      onOpgeslagen?.();
    }).catch(() => setFout('Kopiëren mislukt — selecteer en kopieer de code handmatig uit het vak hieronder.'));
  }

  function stuurNaarMail() {
    if (!code) return;
    if (codeLengte > mailtoLimiet) {
      setFout('De code is te lang voor e-mail (door bijvoorbeeld een foto). Gebruik "Download als bestand" of "Kopieer code" en plak hem zelf in een mail.');
      return;
    }
    const datum = new Date().toLocaleDateString('nl-NL', { day:'numeric', month:'long', year:'numeric' });
    const onderwerp = `Mijn Tulpiaan CV bewaarcode — ${datum}`;
    const body =
`Hieronder staat je Tulpiaan CV bewaarcode. Bewaar deze e-mail goed — zonder de code kun je je CV niet meer terughalen.

Hoe werkt het terugladen?
Ga naar cvmaker.tulpiaan.nl, klik op "Bestaand CV laden", kies "Code plakken" en plak onderstaande code in het veld.

Bewaarcode:

${code}

— Tulpiaan CVmaker`;
    const url = `mailto:?subject=${encodeURIComponent(onderwerp)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
    onOpgeslagen?.();
  }

  const knopGoud = {
    background: accent, color: accentTekst(accent), border:'none', borderRadius:7,
    padding:'10px 16px', fontSize:13, fontWeight:600, cursor: code ? 'pointer' : 'wait',
    fontFamily:"'DM Sans',sans-serif", transition:'all .15s ease', whiteSpace:'nowrap',
  };
  const knopGhost = {
    border:`1.5px solid ${C.grijs200}`, background:'transparent', color:C.grijs600,
    borderRadius:7, padding:'10px 16px', fontSize:13, fontWeight:500, cursor:'pointer',
    fontFamily:"'DM Sans',sans-serif", transition:'all .15s ease',
  };

  return (
    <ModalShell titel="💾 Bewaarcode" onSluit={onSluit}>
      {/* Uitleg */}
      <div style={{ background:'#fdf9f0', border:`1px solid ${C.grijs100}`, borderLeft:`3px solid ${accent}`, borderRadius:'0 8px 8px 0', padding:'12px 14px', marginBottom:18 }}>
        <p style={{ fontSize:13, color:C.grijs800, lineHeight:1.55, marginBottom:6 }}>
          <strong>Bewaar je CV zonder account.</strong> We slaan je gegevens niet op onze servers op — jij bewaart ze zelf via deze code. Plak hem later terug om verder te werken aan je CV.
        </p>
        <p style={{ fontSize:12, color:C.grijs600, lineHeight:1.55, fontStyle:'italic' }}>
          Belangrijk: raak je de code kwijt, dan ben je je CV kwijt. Bewaar hem dus goed (e-mail aan jezelf werkt prima).
        </p>
      </div>

      {/* Optie A — Download */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:14, padding:'12px 0', borderBottom:`1px solid ${C.grijs100}` }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:600, color:C.inkt, marginBottom:2 }}>📁 Download als bestand</div>
          <div style={{ fontSize:12, color:C.grijs600, lineHeight:1.5 }}>Aanbevolen. Klein .tulpiaan-cv bestand dat je later weer kunt uploaden.</div>
        </div>
        <button onClick={downloadAlsBestand} disabled={!code} style={knopGoud}>Download</button>
      </div>

      {/* Optie B — Kopieer */}
      <div style={{ padding:'12px 0', borderBottom:`1px solid ${C.grijs100}` }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:14, marginBottom:10 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:13, fontWeight:600, color:C.inkt, marginBottom:2 }}>📋 Kopieer code</div>
            <div style={{ fontSize:12, color:C.grijs600, lineHeight:1.5 }}>Plak hem ergens veilig — Notion, Wachtwoordmanager, of een notitie-app.</div>
          </div>
          <button onClick={kopieerNaarKlembord} disabled={!code}
            style={ gekopieerd ? { ...knopGoud, background: '#3a7040', color: '#fff' } : knopGoud }>
            {gekopieerd ? '✓ Gekopieerd!' : 'Kopieer'}
          </button>
        </div>
        <textarea readOnly value={code} placeholder="Code wordt gegenereerd…"
          style={{ width:'100%', minHeight:90, maxHeight:140, padding:'8px 10px', fontFamily:'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize:11, lineHeight:1.4, background:C.wit, border:`1px solid ${C.grijs200}`, borderRadius:6, color:C.grijs800, resize:'vertical', wordBreak:'break-all' }} />
        <div style={{ fontSize:11, color:C.grijs400, marginTop:4 }}>{codeLengte.toLocaleString('nl-NL')} tekens</div>
      </div>

      {/* Optie C — Mail */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:14, padding:'12px 0' }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:600, color:C.inkt, marginBottom:2 }}>✉️ Stuur naar mijn e-mail</div>
          <div style={{ fontSize:12, color:C.grijs600, lineHeight:1.5 }}>
            Opent je mail-app met de code erin. {codeLengte > mailtoLimiet && <span style={{ color:C.fout }}>Code is te lang voor mailto — gebruik download of kopieer.</span>}
          </div>
        </div>
        <button onClick={stuurNaarMail} disabled={!code || codeLengte > mailtoLimiet}
          style={{ ...knopGhost, opacity: (codeLengte > mailtoLimiet ? 0.45 : 1), cursor: (codeLengte > mailtoLimiet ? 'not-allowed' : 'pointer') }}>
          Open mail
        </button>
      </div>

      {fout && (
        <div style={{ marginTop:14, background:C.foutBg, border:`1px solid ${C.fout}33`, color:C.fout, padding:'10px 12px', borderRadius:7, fontSize:12, lineHeight:1.5 }}>{fout}</div>
      )}
    </ModalShell>
  );
}

function LaadModal({ accent, onSluit, onLaad }) {
  const [tab, setTab] = useState('bestand');
  const [plak, setPlak] = useState('');
  const [fout, setFout] = useState('');
  const fileRef = useRef(null);

  function verwerkBestand(file) {
    if (!file) return;
    const lezer = new FileReader();
    lezer.onload = ev => {
      try {
        const data = loadSaveCode(String(ev.target.result || ''));
        onLaad(data);
      } catch (e) { setFout(e.message); }
    };
    lezer.onerror = () => setFout('Bestand kon niet gelezen worden.');
    lezer.readAsText(file);
  }

  function laadPlakcode() {
    setFout('');
    try {
      const data = loadSaveCode(plak);
      onLaad(data);
    } catch (e) { setFout(e.message); }
  }

  const tabBtn = isAan => ({
    flex:1, border:'none', borderBottom: isAan ? `2px solid ${accent}` : `2px solid transparent`,
    background:'transparent', color: isAan ? C.inkt : C.grijs400, fontWeight: isAan ? 600 : 500,
    fontSize:13, padding:'10px 0', cursor:'pointer', fontFamily:"'DM Sans', sans-serif", transition:'all .15s ease',
  });

  const knopGoud = {
    background: accent, color: accentTekst(accent), border:'none', borderRadius:7,
    padding:'10px 18px', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif",
  };

  return (
    <ModalShell titel="📂 Bestaand CV laden" onSluit={onSluit}>
      <p style={{ fontSize:13, color:C.grijs600, lineHeight:1.55, marginBottom:14 }}>
        Heb je eerder een bewaarcode gemaakt? Upload het bestand of plak de code om je CV te herstellen.
      </p>

      <div style={{ display:'flex', borderBottom:`1px solid ${C.grijs100}`, marginBottom:16 }}>
        <button onClick={() => { setTab('bestand'); setFout(''); }} style={tabBtn(tab === 'bestand')}>📁 Bestand uploaden</button>
        <button onClick={() => { setTab('plak'); setFout(''); }}    style={tabBtn(tab === 'plak')}>📋 Code plakken</button>
      </div>

      {tab === 'bestand' && (
        <div>
          <input ref={fileRef} type="file" accept=".tulpiaan-cv,.txt,text/plain"
            onChange={e => verwerkBestand(e.target.files?.[0])}
            style={{ display:'none' }} />
          <button onClick={() => fileRef.current?.click()}
            style={{ width:'100%', border:`1.5px dashed ${accent}`, background: accentLicht(accent), borderRadius:8, padding:'24px', fontSize:14, fontWeight:600, color: accent, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>
            Kies bestand…
            <div style={{ fontSize:11, color:C.grijs600, fontWeight:400, marginTop:6 }}>.tulpiaan-cv of .txt</div>
          </button>
        </div>
      )}

      {tab === 'plak' && (
        <div>
          <textarea value={plak} onChange={e => { setPlak(e.target.value); setFout(''); }}
            placeholder="Plak hier je bewaarcode (begint met TLP1-…)"
            style={{ width:'100%', minHeight:140, padding:'10px 12px', fontFamily:'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize:11, lineHeight:1.5, background:C.wit, border:`1.5px solid ${C.grijs200}`, borderRadius:7, color:C.grijs800, resize:'vertical', wordBreak:'break-all' }} />
          <div style={{ display:'flex', justifyContent:'flex-end', marginTop:10 }}>
            <button onClick={laadPlakcode} disabled={!plak.trim()}
              style={{ ...knopGoud, opacity: plak.trim() ? 1 : 0.45, cursor: plak.trim() ? 'pointer' : 'not-allowed' }}>
              Laad CV
            </button>
          </div>
        </div>
      )}

      {fout && (
        <div style={{ marginTop:14, background:C.foutBg, border:`1px solid ${C.fout}33`, color:C.fout, padding:'10px 12px', borderRadius:7, fontSize:12, lineHeight:1.5 }}>{fout}</div>
      )}
    </ModalShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// HOOFD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function CVBuilder() {
  injectGlobalCss();

  const [cv, setCv]                       = useState(beginData);
  const [zichtbaar, setZichtbaar]         = useState(beginZichtbaar);
  const [sectieVolgorde, setSectieVolgorde] = useState([...VASTE]);
  const [eigenSecties, setEigenSecties]   = useState([]);
  const [template, setTemplate]           = useState('donker');
  const [accent, setAccentState]          = useState('#cbad74');
  const [balk, setBalk]                   = useState('#cbad74');
  const [aiSuggestie, setAiSuggestie]     = useState({});
  const [fotoGrootte, setFotoGrootte]     = useState(100);

  // Bewaarcode-state
  const [bewaarOpen, setBewaarOpen]       = useState(false);
  const [laadOpen, setLaadOpen]           = useState(false);
  const [vies, setVies]                   = useState(false); // wijzigingen sinds laatste bewaarcode
  const isAan = useRef(false);            // skip eerste render in dirty-tracker

  const setAccent = useCallback(k => { setAccentState(k); updateAccentCss(k); }, []);
  useEffect(() => { updateAccentCss(accent); }, [accent]);

  // Dirty-tracking — bij iedere wijziging in CV-data zetten we 'vies' op true.
  // Eerste render slaan we over (dat zou anders direct vies markeren).
  useEffect(() => {
    if (!isAan.current) { isAan.current = true; return; }
    setVies(true);
  }, [cv, zichtbaar, sectieVolgorde, eigenSecties, template, accent, balk, fotoGrootte]);

  // beforeunload — waarschuw alleen als er iets te verliezen valt
  useEffect(() => {
    if (!vies) return;
    const handler = e => {
      e.preventDefault();
      e.returnValue = ''; // moderne browsers tonen hun eigen tekst
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [vies]);

  // Snapshot voor bewaarcode — alle state die nodig is om exact terug te keren
  function maakSnapshot() {
    return { cv, zichtbaar, sectieVolgorde, eigenSecties, template, accent, balk, fotoGrootte };
  }

  // State herstellen vanuit bewaarcode
  function pasSnapshotToe(d) {
    if (!d || typeof d !== 'object') return;
    if (d.cv)             setCv({ ...beginData, ...d.cv });
    if (d.zichtbaar)      setZichtbaar({ ...beginZichtbaar, ...d.zichtbaar });
    if (Array.isArray(d.sectieVolgorde)) setSectieVolgorde(d.sectieVolgorde);
    if (Array.isArray(d.eigenSecties))   setEigenSecties(d.eigenSecties);
    if (d.template)       setTemplate(d.template);
    if (d.accent)         setAccentState(d.accent);
    if (d.balk)           setBalk(d.balk);
    if (typeof d.fotoGrootte === 'number') setFotoGrootte(d.fotoGrootte);
    // Dirty-flag opnieuw afstellen: na laden is er geen onbewaarde wijziging
    setTimeout(() => setVies(false), 0);
    setLaadOpen(false);
  }


  const pct = voortgang(cv);

  // ── State helpers ────────────────────────────────────────────────────────────
  const upd     = useCallback((f, v) => setCv(p => ({ ...p, [f]: v })), []);
  const updLijst = useCallback((s, id, f, v) =>
    setCv(p => ({ ...p, [s]: p[s].map(i => i.id === id ? { ...i, [f]: v } : i) })), []);
  const voegToe  = useCallback((s, mk) => setCv(p => ({ ...p, [s]: [...p[s], mk()] })), []);
  const verwijder = useCallback((s, id) => setCv(p => ({ ...p, [s]: p[s].filter(i => i.id !== id) })), []);
  const toggleZichtbaar = useCallback(s => setZichtbaar(p => ({ ...p, [s]: !p[s] })), []);

  function verplaats(id, richting) {
    setSectieVolgorde(p => {
      const i = p.indexOf(id);
      const n = [...p];
      if (richting === 'op' && i > 0)          { [n[i-1], n[i]] = [n[i], n[i-1]]; }
      if (richting === 'neer' && i < n.length-1) { [n[i], n[i+1]] = [n[i+1], n[i]]; }
      return n;
    });
  }

  function voegEigenToe() {
    const s = legeEigen();
    setEigenSecties(p => [...p, s]);
    setSectieVolgorde(p => [...p, s.id]);
    setZichtbaar(p => ({ ...p, [s.id]: true }));
  }
  function verwijderEigen(id) {
    setEigenSecties(p => p.filter(e => e.id !== id));
    setSectieVolgorde(p => p.filter(s => s !== id));
    setZichtbaar(p => { const n = {...p}; delete n[id]; return n; });
  }
  function updEigen(id, veld, waarde) {
    setEigenSecties(p => p.map(e => e.id === id ? { ...e, [veld]: waarde } : e));
  }

  // ── AI ──────────────────────────────────────────────────────────────────────
  const stelSuggestie = (k, t) => setAiSuggestie(p => ({ ...p, [k]: t }));
  function neemOver(sleutel, veld, sectie, id) {
    const t = aiSuggestie[sleutel];
    if (!t) return;
    if (sectie && id) updLijst(sectie, id, veld, t);
    else upd(veld, t);
    stelSuggestie(sleutel, '');
  }

  // ── Foto ─────────────────────────────────────────────────────────────────────
  function laadFoto(e) {
    const f = e.target.files[0];
    if (!f) return;

    const lezer = new FileReader();
    lezer.onload = ev => {
      const img = new Image();
      img.onload = () => {
        // Vierkant croppen vanuit het midden
        const grootte = Math.min(img.width, img.height);
        const sx = (img.width - grootte) / 2;
        const sy = (img.height - grootte) / 2;

        // Doelresolutie 400x400 — ruim genoeg voor print bij 150dpi
        const DOEL = 400;
        const canvas = document.createElement('canvas');
        canvas.width = DOEL;
        canvas.height = DOEL;
        const ctx = canvas.getContext('2d');

        // Witte achtergrond voor JPEG (geen transparantie ondersteund)
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, DOEL, DOEL);

        // Hoge kwaliteit resizing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, sx, sy, grootte, grootte, 0, 0, DOEL, DOEL);

        // JPEG met kwaliteit 0.85 — beste balans tussen grootte en kwaliteit
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        upd('foto', dataUrl);
      };
      img.onerror = () => alert('Foto kon niet geladen worden — probeer een ander bestand.');
      img.src = ev.target.result;
    };
    lezer.onerror = () => alert('Bestand kon niet gelezen worden.');
    lezer.readAsDataURL(f);
  }

  // ── Template render ──────────────────────────────────────────────────────────
  const tplProps = { cv, zichtbaar, sectieVolgorde, eigenSecties, accent, balk, fotoGrootte };
  function renderTemplate() {
    if (template === 'gesplitst') return <TemplateGesplitst {...tplProps} />;
    if (template === 'tijdlijn')  return <TemplateTijdlijn  {...tplProps} />;
    return <TemplateDonker {...tplProps} />;
  }

  // ── Formulier per sectie ──────────────────────────────────────────────────────
  function renderFormSectie(sid, idx) {
    const isEerste = idx === 0;
    const isLaatste = idx === sectieVolgorde.length - 1;
    const pijlProps = { isEerste, isLaatste, onOp: () => verplaats(sid, 'op'), onNeer: () => verplaats(sid, 'neer') };

    if (sid === 'werkervaring') return (
      <FormSectie key={sid} titel="Werkervaring" zichtbaar={zichtbaar.werkervaring} onToggle={() => toggleZichtbaar('werkervaring')} accent={accent} {...pijlProps}>
        {cv.werkervaring.map((w, wi) => (
          <div key={w.id} style={{ marginBottom:20, paddingBottom:18, borderBottom: wi < cv.werkervaring.length-1 ? `1px dashed ${C.grijs100}` : 'none' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
              <span style={{ fontSize:11, fontWeight:600, color:C.grijs600 }}>Positie {wi+1}</span>
              {cv.werkervaring.length > 1 && <button className="verwijder" onClick={() => verwijder('werkervaring', w.id)} style={{ border:`1px solid ${C.grijs200}`, background:'none', borderRadius:5, padding:'3px 9px', fontSize:11, color:C.grijs400, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'all .15s ease' }}>Verwijder</button>}
            </div>
            <Rij>
              <Veld label="Functietitel"><input style={inputStijl} value={w.functie} onChange={e => updLijst('werkervaring', w.id, 'functie', e.target.value)} placeholder="Marketingmanager" /></Veld>
              <Veld label="Bedrijf"><input style={inputStijl} value={w.bedrijf} onChange={e => updLijst('werkervaring', w.id, 'bedrijf', e.target.value)} placeholder="Naam bedrijf" /></Veld>
            </Rij>
            <Veld label="Periode"><input style={inputStijl} value={w.periode} onChange={e => updLijst('werkervaring', w.id, 'periode', e.target.value)} placeholder="jan 2021 – heden" /></Veld>
            <Veld label="Korte intro (1 zin)">
              <input style={inputStijl} value={w.intro} onChange={e => updLijst('werkervaring', w.id, 'intro', e.target.value)} placeholder="Leidde het marketingteam van 8 personen…" />
              <AiKnop type="intro" tekst={w.intro} accent={accent} onResultaat={t => stelSuggestie(`wi_${w.id}`, t)} />
              <AiSuggestie tekst={aiSuggestie[`wi_${w.id}`]} accent={accent} onOvernemen={() => neemOver(`wi_${w.id}`, 'intro', 'werkervaring', w.id)} onNegeren={() => stelSuggestie(`wi_${w.id}`, '')} />
            </Veld>
            <Veld label="Bullet points (één per regel, 2–5)">
              <textarea style={taStijl} rows={4} value={w.bullets} onChange={e => updLijst('werkervaring', w.id, 'bullets', e.target.value)} placeholder={"Ontwikkelde campagnestrategie voor 3 productlijnen\nVerkortte time-to-market met 6 weken\nCoördineerde samenwerking met externe bureaus"} />
              <AiKnop type="bullets" tekst={w.bullets} accent={accent} onResultaat={t => stelSuggestie(`wb_${w.id}`, t)} />
              <AiSuggestie tekst={aiSuggestie[`wb_${w.id}`]} accent={accent} onOvernemen={() => neemOver(`wb_${w.id}`, 'bullets', 'werkervaring', w.id)} onNegeren={() => stelSuggestie(`wb_${w.id}`, '')} />
            </Veld>
            <Veld label="Resultaten (optioneel)">
              <input style={inputStijl} value={w.resultaten} onChange={e => updLijst('werkervaring', w.id, 'resultaten', e.target.value)} placeholder="Omzet +23% YoY, team gegroeid van 4 naar 8 fte" />
            </Veld>
          </div>
        ))}
        <button className="toevoeg" onClick={() => voegToe('werkervaring', legeWerk)} style={{ width:'100%', border:`1.5px dashed ${C.grijs200}`, background:'none', borderRadius:7, padding:'8px', fontSize:13, color:C.grijs600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'all .15s ease' }}>+ Werkervaring toevoegen</button>
      </FormSectie>
    );

    if (sid === 'opleiding') return (
      <FormSectie key={sid} titel="Opleiding" zichtbaar={zichtbaar.opleiding} onToggle={() => toggleZichtbaar('opleiding')} accent={accent} {...pijlProps}>
        {cv.opleiding.map((o, oi) => (
          <div key={o.id} style={{ marginBottom:14, paddingBottom:12, borderBottom: oi < cv.opleiding.length-1 ? `1px dashed ${C.grijs100}` : 'none' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
              <span style={{ fontSize:11, fontWeight:600, color:C.grijs600 }}>Opleiding {oi+1}</span>
              {cv.opleiding.length > 1 && <button className="verwijder" onClick={() => verwijder('opleiding', o.id)} style={{ border:`1px solid ${C.grijs200}`, background:'none', borderRadius:5, padding:'3px 9px', fontSize:11, color:C.grijs400, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'all .15s ease' }}>Verwijder</button>}
            </div>
            <Rij>
              <Veld label="Richting"><input style={inputStijl} value={o.richting} onChange={e => updLijst('opleiding', o.id, 'richting', e.target.value)} placeholder="Communicatiewetenschap" /></Veld>
              <Veld label="Instelling"><input style={inputStijl} value={o.instelling} onChange={e => updLijst('opleiding', o.id, 'instelling', e.target.value)} placeholder="UvA" /></Veld>
            </Rij>
            <Veld label="Periode"><input style={inputStijl} value={o.periode} onChange={e => updLijst('opleiding', o.id, 'periode', e.target.value)} placeholder="2018 – 2022" /></Veld>
          </div>
        ))}
        <button className="toevoeg" onClick={() => voegToe('opleiding', legeOpl)} style={{ width:'100%', border:`1.5px dashed ${C.grijs200}`, background:'none', borderRadius:7, padding:'8px', fontSize:13, color:C.grijs600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'all .15s ease' }}>+ Opleiding toevoegen</button>
      </FormSectie>
    );

    if (sid === 'vaardigheden') return (
      <FormSectie key={sid} titel="Vaardigheden" zichtbaar={zichtbaar.vaardigheden} onToggle={() => toggleZichtbaar('vaardigheden')} accent={accent} {...pijlProps}>
        <Veld label="Kommagescheiden"><textarea style={taStijl} rows={3} value={cv.vaardigheden} onChange={e => upd('vaardigheden', e.target.value)} placeholder="Projectmanagement, Excel, Presenteren…" /></Veld>
      </FormSectie>
    );

    if (sid === 'talen') return (
      <FormSectie key={sid} titel="Talen" zichtbaar={zichtbaar.talen} onToggle={() => toggleZichtbaar('talen')} accent={accent} {...pijlProps}>
        {cv.talen.map((t, ti) => (
          <div key={t.id} style={{ display:'flex', gap:10, alignItems:'flex-end', marginBottom:10 }}>
            <div style={{ flex:1 }}><Veld label={ti===0?'Taal':''}><input style={inputStijl} value={t.taal} onChange={e => updLijst('talen', t.id, 'taal', e.target.value)} placeholder="Nederlands" /></Veld></div>
            <div style={{ width:110 }}><Veld label={ti===0?'Niveau':''}><select style={inputStijl} value={t.niveau} onChange={e => updLijst('talen', t.id, 'niveau', e.target.value)}>{niveaus.map(n => <option key={n}>{n}</option>)}</select></Veld></div>
            {cv.talen.length > 1 && <button className="verwijder" onClick={() => verwijder('talen', t.id)} style={{ border:`1px solid ${C.grijs200}`, background:'none', borderRadius:5, padding:'7px 9px', fontSize:12, color:C.grijs400, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", marginBottom:14, transition:'all .15s ease' }}>✕</button>}
          </div>
        ))}
        <button className="toevoeg" onClick={() => voegToe('talen', legeTaal)} style={{ border:`1.5px dashed ${C.grijs200}`, background:'none', borderRadius:7, padding:'7px 13px', fontSize:13, color:C.grijs600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'all .15s ease' }}>+ Taal</button>
      </FormSectie>
    );

    if (sid === 'certificaten') return (
      <FormSectie key={sid} titel="Certificaten" zichtbaar={zichtbaar.certificaten} onToggle={() => toggleZichtbaar('certificaten')} accent={accent} {...pijlProps}>
        {cv.certificaten.map((cert, ci) => (
          <div key={cert.id} style={{ display:'flex', gap:10, alignItems:'flex-end', marginBottom:10 }}>
            <div style={{ flex:1 }}><Veld label={ci===0?'Naam':''}><input style={inputStijl} value={cert.naam} onChange={e => updLijst('certificaten', cert.id, 'naam', e.target.value)} placeholder="PRINCE2 Foundation" /></Veld></div>
            <div style={{ width:80 }}><Veld label={ci===0?'Jaar':''}><input style={inputStijl} value={cert.jaar} onChange={e => updLijst('certificaten', cert.id, 'jaar', e.target.value)} placeholder="2023" /></Veld></div>
            {cv.certificaten.length > 1 && <button className="verwijder" onClick={() => verwijder('certificaten', cert.id)} style={{ border:`1px solid ${C.grijs200}`, background:'none', borderRadius:5, padding:'7px 9px', fontSize:12, color:C.grijs400, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", marginBottom:14, transition:'all .15s ease' }}>✕</button>}
          </div>
        ))}
        <button className="toevoeg" onClick={() => voegToe('certificaten', legeCert)} style={{ border:`1.5px dashed ${C.grijs200}`, background:'none', borderRadius:7, padding:'7px 13px', fontSize:13, color:C.grijs600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'all .15s ease' }}>+ Certificaat</button>
      </FormSectie>
    );

    if (sid === 'vrijwilligerswerk') return (
      <FormSectie key={sid} titel="Vrijwilligerswerk" zichtbaar={zichtbaar.vrijwilligerswerk} onToggle={() => toggleZichtbaar('vrijwilligerswerk')} accent={accent} {...pijlProps}>
        {cv.vrijwilligerswerk.map((v, vi) => (
          <div key={v.id} style={{ marginBottom:14, paddingBottom:12, borderBottom: vi < cv.vrijwilligerswerk.length-1 ? `1px dashed ${C.grijs100}` : 'none' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
              <span style={{ fontSize:11, fontWeight:600, color:C.grijs600 }}>Entry {vi+1}</span>
              {cv.vrijwilligerswerk.length > 1 && <button className="verwijder" onClick={() => verwijder('vrijwilligerswerk', v.id)} style={{ border:`1px solid ${C.grijs200}`, background:'none', borderRadius:5, padding:'3px 9px', fontSize:11, color:C.grijs400, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'all .15s ease' }}>Verwijder</button>}
            </div>
            <Rij>
              <Veld label="Rol"><input style={inputStijl} value={v.rol} onChange={e => updLijst('vrijwilligerswerk', v.id, 'rol', e.target.value)} placeholder="Bestuurslid" /></Veld>
              <Veld label="Organisatie"><input style={inputStijl} value={v.organisatie} onChange={e => updLijst('vrijwilligerswerk', v.id, 'organisatie', e.target.value)} placeholder="Stichting XYZ" /></Veld>
            </Rij>
            <Veld label="Periode"><input style={inputStijl} value={v.periode} onChange={e => updLijst('vrijwilligerswerk', v.id, 'periode', e.target.value)} placeholder="2020 – heden" /></Veld>
          </div>
        ))}
        <button className="toevoeg" onClick={() => voegToe('vrijwilligerswerk', legeVrij)} style={{ width:'100%', border:`1.5px dashed ${C.grijs200}`, background:'none', borderRadius:7, padding:'8px', fontSize:13, color:C.grijs600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'all .15s ease' }}>+ Vrijwilligerswerk</button>
      </FormSectie>
    );

    // Eigen sectie
    const eigen = eigenSecties.find(e => e.id === sid);
    if (eigen) return (
      <FormSectie key={sid} titel={eigen.titel || 'Eigen sectie'} zichtbaar={zichtbaar[sid]} onToggle={() => toggleZichtbaar(sid)} accent={accent} {...pijlProps}>
        <Veld label="Sectietitel"><input style={inputStijl} value={eigen.titel} onChange={e => updEigen(sid, 'titel', e.target.value)} placeholder="Bijv. Publicaties, Hobby's, Projecten…" /></Veld>
        <Veld label="Inhoud">
          <textarea style={taStijl} rows={4} value={eigen.tekst} onChange={e => updEigen(sid, 'tekst', e.target.value)} placeholder="Vrije tekst…" />
        </Veld>
        <button className="verwijder" onClick={() => verwijderEigen(sid)} style={{ border:`1px solid ${C.grijs200}`, background:'none', borderRadius:6, padding:'6px 12px', fontSize:12, color:C.grijs400, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'all .15s ease' }}>Sectie verwijderen</button>
      </FormSectie>
    );
    return null;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ minHeight:'100vh', background:C.papier, display:'flex', flexDirection:'column', fontFamily:"'DM Sans', sans-serif" }}>

      {/* Header */}
      <header className="geen-print" style={{ background:C.wit, borderBottom:`1px solid ${C.grijs100}`, padding:'0 24px', height:56, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <img src={logo} alt="Tulpiaan" style={{ width:28, height:28 }} />
          <span style={{ fontFamily:"'Lora', serif", fontWeight:600, fontSize:16, color:C.inkt }}>CV Builder</span>
          <span style={{ fontSize:12, color:C.grijs400, marginLeft:2 }}>by Tulpiaan</span>
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <button className="exp-ghost" onClick={() => setLaadOpen(true)}
            style={{ border:`1.5px solid ${C.grijs200}`, background:'transparent', borderRadius:7, padding:'7px 12px', fontSize:13, fontWeight:500, color:C.grijs600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'all .15s ease' }}>
            📂 CV laden
          </button>
          <button onClick={() => setBewaarOpen(true)}
            style={{ border:`1.5px solid ${accent}`, background: accentLicht(accent), color: accent, borderRadius:7, padding:'7px 14px', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'all .15s ease' }}>
            💾 Bewaarcode
          </button>
          <button className="exp-ghost" onClick={() => {
            const logoAbsUrl = logo.startsWith('data:') || logo.startsWith('http') ? logo : window.location.origin + logo;
            exporteerPDF(cv, zichtbaar, sectieVolgorde, eigenSecties, template, accent, balk, logoAbsUrl, fotoGrootte);
          }} style={{ border:`1.5px solid ${C.grijs200}`, background:'transparent', borderRadius:7, padding:'7px 14px', fontSize:13, fontWeight:600, color:C.grijs600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'all .15s ease' }}>PDF</button>
          <button onClick={() => exporteerWord(cv, zichtbaar, sectieVolgorde, eigenSecties)} style={{ background: accent, color: accentTekst(accent), border:'none', borderRadius:7, padding:'7px 16px', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", boxShadow:'0 2px 10px rgba(0,0,0,.15)', transition:'all .2s ease' }}>Word (.docx)</button>
        </div>
      </header>

      <VoortgangsBalk pct={pct} accent={accent} />

      {/* Split layout */}
      <div className="split-layout" style={{ flex:1, display:'flex', overflow:'hidden' }}>

        {/* ── FORMULIER ── */}
        <div className="form-panel geen-print" style={{ width:430, flexShrink:0, overflowY:'auto', borderRight:`1px solid ${C.grijs100}`, padding:'18px 18px 40px' }}>

          {/* Persoonlijk profiel */}
          <FormSectie titel="Persoonlijk profiel" zichtbaar={true} accent={accent}>
            <Veld label="Volledige naam"><input style={inputStijl} value={cv.naam} onChange={e => upd('naam', e.target.value)} placeholder="Voor- en achternaam" /></Veld>
            <Veld label="Functietitel"><input style={inputStijl} value={cv.functietitel} onChange={e => upd('functietitel', e.target.value)} placeholder="Senior Marketingmanager" /></Veld>
            <Veld label="Foto (optioneel)">
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                {cv.foto && <img src={cv.foto} alt="" style={{ width:40, height:40, borderRadius:'50%', objectFit:'cover', border:`2px solid ${C.grijs200}` }} />}
                <input type="file" accept="image/*" onChange={laadFoto} style={{ fontSize:13, color:C.grijs600 }} />
              </div>
            </Veld>
            {cv.foto && (
              <Veld label="Fotogrootte">
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <input type="range" min={60} max={160} value={fotoGrootte} onChange={e => setFotoGrootte(Number(e.target.value))} style={{ flex:1, accentColor: accent }} />
                  <span style={{ fontSize:12, color:C.grijs600, minWidth:36, textAlign:'right' }}>{fotoGrootte}px</span>
                </div>
              </Veld>
            )}
            <Rij>
              <Veld label="E-mail"><input style={inputStijl} value={cv.email} onChange={e => upd('email', e.target.value)} placeholder="naam@mail.nl" /></Veld>
              <Veld label="Telefoon"><input style={inputStijl} value={cv.telefoon} onChange={e => upd('telefoon', e.target.value)} placeholder="+31 6 …" /></Veld>
            </Rij>
            <Veld label="Woonplaats"><input style={inputStijl} value={cv.locatie} onChange={e => upd('locatie', e.target.value)} placeholder="Amsterdam" /></Veld>
            <Rij>
              <Veld label="LinkedIn"><input style={inputStijl} value={cv.linkedin} onChange={e => upd('linkedin', e.target.value)} placeholder="linkedin.com/in/…" /></Veld>
              <Veld label="Website"><input style={inputStijl} value={cv.website} onChange={e => upd('website', e.target.value)} placeholder="jouwsite.nl" /></Veld>
            </Rij>
          </FormSectie>

          {/* Samenvatting */}
          <FormSectie titel="Persoonlijke samenvatting" zichtbaar={true} accent={accent}>
            <Veld label="Samenvatting">
              <textarea style={taStijl} rows={4} value={cv.samenvatting} onChange={e => upd('samenvatting', e.target.value)} placeholder="3–4 zinnen over wie je bent en wat je zoekt…" />
            </Veld>
            <AiKnop type="samenvatting" tekst={cv.samenvatting} accent={accent} onResultaat={t => stelSuggestie('samenvatting', t)} />
            <AiSuggestie tekst={aiSuggestie['samenvatting']} accent={accent} onOvernemen={() => neemOver('samenvatting', 'samenvatting')} onNegeren={() => stelSuggestie('samenvatting', '')} />
          </FormSectie>

          {/* Geordende secties */}
          {sectieVolgorde.map((sid, idx) => renderFormSectie(sid, idx))}

          {/* Eigen sectie toevoegen */}
          <button className="toevoeg" onClick={voegEigenToe} style={{ width:'100%', border:`1.5px dashed ${accent}`, background:'none', borderRadius:8, padding:'11px', fontSize:13, fontWeight:600, color: accent, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'all .15s ease', marginTop:4 }}>
            + Voeg eigen sectie toe
          </button>
        </div>

        {/* ── PREVIEW ── */}
        <div className="preview-panel" style={{ flex:1, overflowY:'auto', padding:'20px 24px 40px', display:'flex', flexDirection:'column', alignItems:'center', minWidth:0 }}>

          <div className="geen-print" style={{ alignSelf:'flex-start', marginBottom:14, width:'100%' }}>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:12 }}>
              <KleurKiezer label="Balkkleur"   waarde={balk}   setWaarde={setBalk} />
              <KleurKiezer label="Accentkleur" waarde={accent} setWaarde={setAccent} />
            </div>
            <div style={{ display:'flex', gap:8 }}>
              {[['donker','Donker & Krachtig'],['gesplitst','Gesplitst & Zakelijk'],['tijdlijn','Tijdlijn & Strak']].map(([id, label]) => (
                <button key={id} className="tpl-btn" onClick={() => setTemplate(id)}
                  style={{ border:`1.5px solid ${template===id ? accent : C.grijs200}`, background: template===id ? accentLicht(accent) : C.wit, color: template===id ? accent : C.grijs600, borderRadius:7, padding:'7px 14px', fontSize:12, fontWeight: template===id ? 700 : 400, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'all .15s ease' }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="cv-preview" style={{ width:'100%', maxWidth:760, background:C.wit, boxShadow:'0 4px 32px rgba(0,0,0,.1)', borderRadius:3, overflow:'hidden', animation:'fadeUp .3s ease' }}>
            {renderTemplate()}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="geen-print" style={{ background:C.wit, borderTop:`1px solid ${C.grijs100}`, padding:'14px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, flexWrap:'wrap', flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <img src={logo} alt="Tulpiaan" style={{ width:18, height:18 }} />
          <span style={{ fontSize:11, color:C.grijs600, fontWeight:600 }}>Tulpiaan CV-bouwer</span>
        </div>
        <div style={{ fontSize:11, color:C.grijs400, textAlign:'center', flex:1, minWidth:200 }}>
          Gratis CV-tool · Geen account vereist · Jouw gegevens blijven van jou
        </div>
        <div style={{ display:'flex', gap:14, fontSize:11 }}>
          <a href="/privacy" style={{ color:C.grijs600, textDecoration:'none' }}>Privacy</a>
          <a href="https://tulpiaan.nl" target="_blank" rel="noopener noreferrer" style={{ color:C.grijs600, textDecoration:'none' }}>Over Tulpiaan</a>
          <a href="mailto:info@tulpiaan.nl" style={{ color:C.grijs600, textDecoration:'none' }}>Contact</a>
        </div>
      </footer>

      {/* Bewaarcode-modals */}
      {bewaarOpen && (
        <BewaarcodeModal
          snapshot={maakSnapshot()}
          cv={cv}
          accent={accent}
          onSluit={() => setBewaarOpen(false)}
          onOpgeslagen={() => setVies(false)}
        />
      )}
      {laadOpen && (
        <LaadModal
          accent={accent}
          onSluit={() => setLaadOpen(false)}
          onLaad={pasSnapshotToe}
        />
      )}
    </div>
  );
}
